import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationInfo, supabaseLocationService } from '../../services/supabaseLocationService';
import { supabaseClient } from '../../services/supabase';
import { GLOBAL_ADMIN_LOCATION } from '../../constants/adminSettings';
import { OVERVIEW_SECTIONS } from '../../config/adminOverviewConfig';
import { LocationSelector } from '../LocationSelector';
import { HeaderLocationSelector } from '../layout/HeaderLocationSelector';
import { HomePage } from '../home/HomePage';
import { PageHeader } from '../layout/PageHeader';
import { useLocation } from '../../contexts/LocationContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useAuth } from '../../contexts/AuthContext';
import { BurdenBenefitsChart } from '../charts/BurdenBenefitsChart';
import { fetchElectricitySeries, fetchNaturalGasSeries } from '../charts/EnergyUseSupabase';
import { fetchGHGInventoryData } from '../charts/GHGInventorySupabase';
import { resolveByCapName } from '../../domains/ghgInventoryReport/crosswalk';
import { DataService } from '../../services/dataService';
import { VMTChart } from '../charts/VMTChart';
import { TransportationModeShare } from '../charts/TransportationModeShare';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import WasteTonnageChart from '../charts/WasteTonnageChart';
import LandfillEmissionsStackedChart from '../charts/LandfillEmissionsStackedChart';
import IncomeByRaceBar from '../charts/IncomeByRaceBar';
import HousingBurdenSection from '../charts/HousingBurdenSection';
import { CumulativeSolarCapacityChart } from '../charts/CumulativeSolarCapacityChart';
import { SectorBasedGHGInventory } from '../charts/SectorBasedGHGInventory';
import { ConsumptionBasedGHGInventory } from '../charts/ConsumptionBasedGHGInventory';
import { fetchConsumptionBasedGHGData } from '../../services/consumptionBasedGHGService';
import { setVMTDataFromTransportation } from '../../services/vmtDataService';
import DataDownloadModal from '../auth/DataDownloadModal';
import { useMemberExportsLocked } from '../../hooks/useMemberExportsLocked';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { displayPercentile } from '../../utils/percentileUtils';
import { fetchFossilHeatingBurdenPercentile } from '../../services/fossilHeatingPercentileService';
import { fetchEmploymentRateBenefitPercentile } from '../../services/employmentRatePercentileService';
import { fetchPublicTransitLivabilityPercentile } from '../../services/publicTransitPercentileService';
import TransportationPeerComparisonCard from '../charts/TransportationPeerComparisonCard';

import './Overview.css';

import equityIndexBg from '../../assets/overview/equity-index-bg.jpg';
import buildingsEnergyBg from '../../assets/overview/buildings-energy-bg.jpg';
import transportationBg from '../../assets/overview/transportation-bg.jpg';
import wasteBg from '../../assets/overview/waste-bg.jpg';
import peopleEquityBg from '../../assets/overview/people-equity-bg.jpg';
import ghgInventoryBg from '../../assets/overview/ghg-inventory-bg.jpg';

const SECTION_BG_IMAGES: Record<string, string> = {
  'equity-index': equityIndexBg,
  'buildings': buildingsEnergyBg,
  'transportation': transportationBg,
  'waste': wasteBg,
  'people-equity': peopleEquityBg,
  'ghg-inventory': ghgInventoryBg,
};

// Extend Window interface for Overview loading state
declare global {
  interface Window {
    overviewFullyLoaded?: boolean;
  }
}

/** Ordinal suffix for ranking text: 1 → "1st", 2 → "2nd", 3 → "3rd", etc. */
function ordinalRank(n: number): string {
  const s = n % 100;
  const suffix = s >= 11 && s <= 13 ? 'th' : (n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th');
  return `${n}${suffix}`;
}

/** Among n peers, rank 1 = best. Share of peer distribution “above” lower-ranked places (~95th ≈ top 5% when rank is 24 of 459). */
function peerPercentileAmongRank(rank: number, n: number): number | null {
  if (!Number.isFinite(rank) || !Number.isFinite(n) || n <= 1 || rank < 1 || rank > n) return null;
  return (100 * (n - rank)) / (n - 1);
}

/** Visually aligns with Burdens (red) → mid (yellow) → Benefits (emerald): low peer percentile = more red, high = more green. */
function equityPeerPercentileColor(percentile0to100: number): string {
  const x = Math.max(0, Math.min(100, percentile0to100));
  const h = Math.round((x / 100) * 120);
  return `hsl(${h}, 72%, 36%)`;
}

/** Peer percentile among all peers in `overallPeerRankMeta` — same basis as Climate Equity Index title. */
function peerPercentileFromOverallMeta(
  meta: { n: number; rankByLocation: Map<string, number> } | null,
  locationName: string
): number | null {
  if (!meta || meta.n <= 1) return null;
  const r = meta.rankByLocation.get(locationName.trim().toLowerCase());
  if (typeof r !== 'number' || r < 1 || r > meta.n) return null;
  return peerPercentileAmongRank(r, meta.n);
}

interface LivabilityRankingRow {
  rank: number;
  locationName: string;
  geofips: string;
  score: number;
  burdens: number;
  benefits: number;
}

type RankingRowCount = 5 | 10 | 25 | 50 | 100 | 'all';

/** Category config shared by Climate Equity Over Time chart and Climate Equity Rankings table. */
const LIVABILITY_BURDEN_CATEGORIES: { id: string; label: string; sub: { key: string; label: string }[] }[] = [
  { id: 'ghg', label: 'GHG Emissions', sub: [{ key: 'ldv_emissions_percentile', label: 'LDV Emissions' }, { key: 'hdv_emissions_percentile', label: 'HDV Emissions' }, { key: 'elec_total_emissions_percentile', label: 'Electricity' }, { key: 'ng_total_emissions_percentile', label: 'Natural Gas' }, { key: 'waste_emissions_percentile', label: 'Waste' }, { key: 'offroad_emissions_percentile', label: 'Offroad' }]},
  { id: 'pollution', label: 'Pollution', sub: [{ key: 'diesel_pm_percentile', label: 'Diesel PM' }, { key: 'pm2_5_percentile', label: 'PM2.5' }, { key: 'ozone_percentile', label: 'Ozone' }, { key: 'tox_release_percentile', label: 'Toxic Releases' }, { key: 'haz_waste_percentile', label: 'Hazardous Waste' }]},
  { id: 'health', label: 'Health', sub: [{ key: 'asthma_percentile', label: 'Asthma' }, { key: 'cardiovascular_disease_percentile', label: 'Cardiovascular' }, { key: 'low_birth_weight_percentile', label: 'Low Birth Weight' }]},
  { id: 'transportation', label: 'Transportation', sub: [{ key: 'traffic_percentile', label: 'Traffic' }, { key: 'commute_time_percentile', label: 'Commute Time' }]},
  { id: 'built_env', label: 'Built Environment', sub: [{ key: 'housing_burden_renter_percentile', label: 'Renter Burden' }, { key: 'housing_burden_owner_percentile', label: 'Owner Burden' }, { key: 'displacement_risk_percentile', label: 'Displacement Risk' }, { key: 'percent_fossil_heating_percentile', label: 'Percent Fossil Heating' }]}
];
const LIVABILITY_BENEFIT_CATEGORIES: { id: string; label: string; sub: { key: string; label: string }[] }[] = [
  { id: 'equity', label: 'Equity', sub: [{ key: 'income_equity_percentile', label: 'Income Equity' }, { key: 'education_equity_percentile', label: 'Education Equity' }, { key: 'wage_equity_percentile', label: 'Wage Equity' }]},
  { id: 'socioeconomic', label: 'Socioeconomic', sub: [{ key: 'median_household_income_percentile', label: 'Median Income' }, { key: 'employment_rate_percentile', label: 'Employment Rate' }, { key: 'educational_attainment_percentile', label: 'Educational Attainment' }, { key: 'health_insurance_percentile', label: 'Health Insurance' }, { key: 'job_wage_percentile', label: 'Job Wages' }]},
  { id: 'mobility', label: 'Mobility', sub: [{ key: 'public_transit_percentile', label: 'Public Transit' }, { key: 'ev_per_1000_people_percentile', label: 'EVs per 1,000' }, { key: 'ev_charging_percentile', label: 'EV Charging' }]},
  { id: 'built_env', label: 'Built Environment', sub: [{ key: 'solar_pv_percentile', label: 'Solar PV' }, { key: 'heat_pump_percentile', label: 'Heat Pump' }]},
  { id: 'community_resources', label: 'Community Resources', sub: [{ key: 'tree_canopy_percentile', label: 'Tree Canopy' }, { key: 'benefit_community_resources_percentile', label: 'Community Resources' }]}
];

interface LivabilityRankingSourceRow {
  location_name: string | null;
  geofips: string | null;
  year: number | null;
  total_score_percentile: number | null;
  total_burdens_percentile: number | null;
  total_benefits_percentile: number | null;
  [key: string]: string | number | null | undefined;
}

interface LivabilityTrendRow {
  year: number;
  total_score_percentile: number | null;
  total_burdens_percentile: number | null;
  total_benefits_percentile: number | null;
}

interface LivabilityTrendRawRow extends LivabilityTrendRow {
  ldv_emissions_percentile?: number | null;
  hdv_emissions_percentile?: number | null;
  elec_total_emissions_percentile?: number | null;
  ng_total_emissions_percentile?: number | null;
  waste_emissions_percentile?: number | null;
  offroad_emissions_percentile?: number | null;
  diesel_pm_percentile?: number | null;
  pm2_5_percentile?: number | null;
  ozone_percentile?: number | null;
  tox_release_percentile?: number | null;
  haz_waste_percentile?: number | null;
  asthma_percentile?: number | null;
  cardiovascular_disease_percentile?: number | null;
  low_birth_weight_percentile?: number | null;
  traffic_percentile?: number | null;
  commute_time_percentile?: number | null;
  transportation_cost_burden_percentile?: number | null;
  housing_burden_renter_percentile?: number | null;
  housing_burden_owner_percentile?: number | null;
  housing_burden_percentile?: number | null;
  displacement_risk_percentile?: number | null;
  percent_fossil_heating_percentile?: number | null;
  income_equity_percentile?: number | null;
  education_equity_percentile?: number | null;
  wage_equity_percentile?: number | null;
  median_household_income_percentile?: number | null;
  employment_rate_percentile?: number | null;
  job_wage_percentile?: number | null;
  educational_attainment_percentile?: number | null;
  health_insurance_percentile?: number | null;
  ev_per_1000_people_percentile?: number | null;
  ev_charging_percentile?: number | null;
  public_transit_percentile?: number | null;
  solar_pv_percentile?: number | null;
  heat_pump_percentile?: number | null;
  tree_canopy_percentile?: number | null;
  benefit_community_resources_percentile?: number | null;
}

// Section IDs for animation (intro + scroll reveal) - stable order
const OVERVIEW_SECTION_IDS = ['equity-index', 'buildings', 'transportation', 'waste', 'people-equity', 'ghg-inventory'] as const;

/**
 * Hook that defers mounting heavy children until the element is near the viewport.
 * Once activated, stays mounted to avoid re-fetching data.
 */
/**
 * Defers mounting heavy children until the element is near the viewport.
 * When `eager` is true (e.g. during initial data load), mounts immediately.
 * Once activated, stays mounted to avoid re-fetching data.
 */
function useLazySection(rootMargin = '200px 0px', eager = false) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = useState(eager);

  useEffect(() => {
    if (eager) { setShouldMount(true); return; }
  }, [eager]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || shouldMount) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldMount, rootMargin, eager]);

  return { sentinelRef, shouldMount };
}

/** Creates a debounced setter that batches rapid updates from chart callbacks. */
function useDebouncedSetter<T>(
  setter: React.Dispatch<React.SetStateAction<T>>,
  key: string,
  debounceTimers: React.MutableRefObject<Record<string, ReturnType<typeof setTimeout> | null>>,
  debounceMs: number
): (value: T) => void {
  const pendingRef = useRef<T | undefined>(undefined);
  return useCallback((value: T) => {
    pendingRef.current = value;
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]!);
    debounceTimers.current[key] = setTimeout(() => {
      setter((pendingRef.current ?? null) as T);
      debounceTimers.current[key] = null;
    }, debounceMs);
  }, [setter, key, debounceTimers, debounceMs]);
}

// Function to calculate stacked data as sum of residential + commercial (same as Energy page)
const calculateStackedData = (resData: any, comData: any, type: string) => {
  if (!resData?.chartData || !comData?.chartData) return null;

  const resLabels = (resData.chartData.labels || []) as number[];
  const comLabels = (comData.chartData.labels || []) as number[];
  
  // Get years that exist in both residential and commercial data
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const allYears: number[] = Array.from(resYears).filter((year: number) => comYears.has(year)).sort();
  
  // Extract data from all datasets (observed, interpolated, extrapolated) for each sector
  const resDatasets = resData.chartData.datasets || [];
  const comDatasets = comData.chartData.datasets || [];
  
  // Create maps for each sector's total values (sum of all data types)
  const resMap = new Map<number, number>();
  const comMap = new Map<number, number>();
  
  // Sum up all data types for residential
  resLabels.forEach((year: number, i: number) => {
    let total = 0;
    resDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    resMap.set(year, total);
  });
  
  // Sum up all data types for commercial
  comLabels.forEach((year: number, i: number) => {
    let total = 0;
    comDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    comMap.set(year, total);
  });

  // Create stacked chart data
  const stackedChartData = {
    labels: allYears,
    datasets: [
      {
        label: 'Residential',
        data: allYears.map(year => resMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderWidth: 1,
        stack: 'total'
      },
      {
        label: 'Commercial',
        data: allYears.map(year => comMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderWidth: 1,
        stack: 'total'
      }
    ]
  };

  // Create chart options for stacked bar chart
  const stackedChartOptions = {
    type: 'bar',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const v = Number(ctx.parsed.y);
            return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
          }
        }
      }
    },
    scales: {
      x: { 
        stacked: true, 
        title: { display: true, text: 'Year' } 
      },
      y: { 
        stacked: true, 
        beginAtZero: true, 
        title: { 
          display: true, 
          text: type === 'Electricity' ? 'kWh' : 'Therms'
        } 
      }
    }
  };

  return {
    chartData: stackedChartData,
    chartOptions: stackedChartOptions
  };
};

/** Parse a value to number, returning null if not finite. */
const num = (v: any): number | null => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);

/** Average of finite numbers in the array, or null if none. */
const avg = (vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

/** Climate equity composite score: (benefits + (100 − burdens)) / 2. Returns null when inputs are missing/non-finite. */
function livabilityScore(benefits: number | null | undefined, burdens: number | null | undefined): number | null {
  const b = Number(benefits);
  const bu = Number(burdens);
  return Number.isFinite(b) && Number.isFinite(bu) ? (b + (100 - bu)) / 2 : null;
}

/** Normalize a 0–1 percentile to 0–100 scale; values already > 1 are returned as-is. */
const normalizePercentile = (p: number): number => (p <= 1 ? p * 100 : p);

/** Prefer DB `total_score_percentile`; otherwise (Benefits + (100 − Burdens)) / 2. Returns 0–100 scale. */
function overallLivabilityPercentileScore(r: {
  total_score_percentile?: number | null;
  total_benefits_percentile?: number | null;
  total_burdens_percentile?: number | null;
}): number | null {
  const tsp = num(r.total_score_percentile);
  if (tsp != null && Number.isFinite(tsp)) return normalizePercentile(tsp);
  return livabilityScore(r.total_benefits_percentile, r.total_burdens_percentile);
}

/**
 * Compute percent change from a base year to the latest year in stacked chart data.
 * Returns null if the base year is missing or has zero total.
 */
function calcPercentChangeSince(
  stackedData: { chartData: { labels: number[]; datasets: { data: number[] }[] } },
  baseYear: number
): number | null {
  const { labels, datasets } = stackedData.chartData;
  if (labels.length === 0 || datasets.length === 0) return null;
  const baseIndex = labels.indexOf(baseYear);
  const latestIndex = labels.length - 1;
  if (baseIndex < 0 || latestIndex < 0) return null;
  const totalBase = datasets.reduce((sum, ds) => sum + (ds.data[baseIndex] || 0), 0);
  const totalLatest = datasets.reduce((sum, ds) => sum + (ds.data[latestIndex] || 0), 0);
  return totalBase > 0 ? ((totalLatest - totalBase) / totalBase) * 100 : null;
}

/** Paginate through a Supabase query, collecting all rows. */
async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
  pageSize = 1000
): Promise<T[]> {
  let from = 0;
  const allRows: T[] = [];
  while (true) {
    const { data, error } = await buildQuery(from, from + pageSize - 1);
    if (error) throw error;
    const pageRows = (data || []) as T[];
    allRows.push(...pageRows);
    if (pageRows.length < pageSize) break;
    from += pageSize;
  }
  return allRows;
}

const moreFiguresButtonStyle: React.CSSProperties = {
  color: '#3b82f6',
  textDecoration: 'none',
  fontSize: '1.2rem',
  fontWeight: '600',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer'
};

const MoreFiguresButton: React.FC<{ path: string; label: string; onClick: (path: string) => void }> = ({ path, label, onClick }) => (
  <div style={{ textAlign: 'right', marginTop: '20px' }}>
    <button
      type="button"
      onClick={() => onClick(path)}
      style={moreFiguresButtonStyle}
      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
    >
      {label} →
    </button>
  </div>
);

interface OverviewProps {
  onLocationSelect: (location: string) => void;
  isLandingPage?: boolean;
}

export const Overview: React.FC<OverviewProps> = ({ onLocationSelect, isLandingPage = false }) => {
  const { selectedLocation, selectedGeofips, locationData: contextLocationData } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile, hasRole, hasAnyRole } = useUserRole();
  const exportsLocked = useMemberExportsLocked();

  // Helper function to handle navigation with loading check
  const handleNavigateWithCheck = (path: string) => {
    const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
    const targetPath = slug ? `/${slug}${path}` : path;

    // Always allow navigation to Transportation page
    if (path === '/transportation') {
      navigate(targetPath);
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      return;
    }
    
    // Check if Overview is fully loaded before allowing navigation to other pages
    if (!overviewFullyLoaded) {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    navigate(targetPath);
    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };
  const [availableLocations, setAvailableLocations] = useState<LocationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptCommunications, setAcceptCommunications] = useState(true);
  const [elecStacked, setElecStacked] = useState<any>(null);
  const [gasStacked, setGasStacked] = useState<any>(null);
  const [elecChangeSince2005, setElecChangeSince2005] = useState<number | null>(null);
  const [gasChangeSince2005, setGasChangeSince2005] = useState<number | null>(null);
  const [cumulativeSolarCapacity, setCumulativeSolarCapacity] = useState<number | null>(null);
  const [ghgInventoryData, setGhgInventoryData] = useState<any>(null);
  const [consumptionGHGData, setConsumptionGHGData] = useState<any>(null);
  const [ghgChartType, setGhgChartType] = useState<'stacked-area' | 'line' | 'pie'>('stacked-area');
  const [consumptionChartType, setConsumptionChartType] = useState<'stacked-area' | 'line' | 'pie'>('stacked-area');
  const [housingBurdenPercentile, setHousingBurdenPercentile] = useState<number | null>(null);
  const [housingBurdenRenterPercentile, setHousingBurdenRenterPercentile] = useState<number | null>(null);
  const [housingBurdenOwnerPercentile, setHousingBurdenOwnerPercentile] = useState<number | null>(null);
  const [fossilHeatingLivabilityPercentile, setFossilHeatingLivabilityPercentile] = useState<number | null>(null);
  const [employmentRateLivabilityPercentile, setEmploymentRateLivabilityPercentile] = useState<number | null>(null);
  const [publicTransitLivabilityPercentile, setPublicTransitLivabilityPercentile] = useState<number | null>(null);
  const [transportationCostBurdenLivabilityPercentile, setTransportationCostBurdenLivabilityPercentile] =
    useState<number | null>(null);
  const [incomeDifferenceRatio, setIncomeDifferenceRatio] = useState<number | null>(null);
  const [showDataDownload, setShowDataDownload] = useState(false);
  const [vmtChangeSince2005, setVmtChangeSince2005] = useState<number | null>(null);
  const [privateVehiclePercent, setPrivateVehiclePercent] = useState<number | null>(null);
  const [evPercent, setEvPercent] = useState<number | null>(null);
  const [wasteChangeSince2005, setWasteChangeSince2005] = useState<number | null>(null);
  const [landfillEmissions2023, setLandfillEmissions2023] = useState<number | null>(null);
  const [vmtDataReady, setVmtDataReady] = useState<boolean>(false);
  const [vmtDataAvailable, setVmtDataAvailable] = useState<boolean>(false);
  const [overviewVMTChartLoaded, setOverviewVMTChartLoaded] = useState<boolean>(false);
  const [overviewFullyLoaded, setOverviewFullyLoaded] = useState<boolean>(false);
  const [overviewSettings, setOverviewSettings] = useState<Record<string, Record<string, boolean>> | null>(null);
  /** Chart ID -> is_enabled. Admin saves with page section_ids (vmt, electricity_consumption); Overview uses overview section_ids (transportation, buildings). Index by chart_id so lookup works. */
  const [overviewSettingsByChartId, setOverviewSettingsByChartId] = useState<Record<string, boolean> | null>(null);
  /** Chart ID -> visible_roles. Same reason as overviewSettingsByChartId. */
  const [overviewVisibilityByChartId, setOverviewVisibilityByChartId] = useState<Record<string, string[] | null> | null>(null);
  const [overviewSettingsLoaded, setOverviewSettingsLoaded] = useState<boolean>(false);
  const [rankingGeographyType, setRankingGeographyType] = useState<'City' | 'County'>('City');
  const [rankingYear, setRankingYear] = useState<number>(2023);
  const [rankingAvailableYears, setRankingAvailableYears] = useState<number[]>([]);
  const [rankingRowCount, setRankingRowCount] = useState<RankingRowCount>(10);
  const [rankingView, setRankingView] = useState<'overall' | 'burdens' | 'benefits'>('overall');
  const [rankingCategory, setRankingCategory] = useState<string>('all');
  const [rankingSubcategory, setRankingSubcategory] = useState<string>('');
  /** Single-column rankings table: full list or a window centered on the selected location. */
  const [rankingDisplayRows, setRankingDisplayRows] = useState<LivabilityRankingRow[]>([]);
  const [rankingDisplayCenteredOnSelection, setRankingDisplayCenteredOnSelection] = useState(false);
  const [rankingTotalRows, setRankingTotalRows] = useState<number>(0);
  /** Maps lowercase location name -> 1-based rank (1=best, N=worst). Enables ranking for any city even when not in displayed top/bottom. */
  const [rankingByLocation, setRankingByLocation] = useState<Map<string, number>>(new Map());
  /** Maps lowercase location name -> overall equity score. Use for title to match table. */
  const [rankingScoreByLocation, setRankingScoreByLocation] = useState<Map<string, number>>(new Map());
  /** Overall equity rank among peers (same geography/year), for hero subtitle percentile — independent of rankings table view. */
  const [overallPeerRankMeta, setOverallPeerRankMeta] = useState<{ n: number; rankByLocation: Map<string, number> } | null>(null);
  const [rankingLoading, setRankingLoading] = useState<boolean>(false);
  const [rankingError, setRankingError] = useState<string | null>(null);
  const [livabilityTrendRows, setLivabilityTrendRows] = useState<LivabilityTrendRawRow[]>([]);
  const [livabilityTrendLoading, setLivabilityTrendLoading] = useState<boolean>(false);
  const [livabilityTrendError, setLivabilityTrendError] = useState<string | null>(null);
  const [livabilityTrendView, setLivabilityTrendView] = useState<'overall' | 'burdens' | 'benefits'>('overall');
  const [equitySideBySide, setEquitySideBySide] = useState(false);
  const [equityCompareView, setEquityCompareView] = useState<'burdens' | 'benefits'>('burdens');
  const [equityCompareLeft, setEquityCompareLeft] = useState('');
  const [equityCompareRight, setEquityCompareRight] = useState('');
  const [livabilityTrendCategory, setLivabilityTrendCategory] = useState<string>('all');
  const rankingDataCacheRef = useRef<Partial<Record<'City' | 'County', LivabilityRankingSourceRow[]>>>({});
  const livabilityTrendCacheRef = useRef<Record<string, LivabilityTrendRawRow[]>>({});
  /** For Climate Equity Over Time: points and ranking change from 2015 to 2023. Loaded when Over Time chart is shown. */
  const [livabilityRankingChangeFrom2005, setLivabilityRankingChangeFrom2005] = useState<{
    rankStart: number;
    rankEnd: number;
    startYear: number;
    endYear: number;
    rankDelta: number;
    pointsStart: number;
    pointsEnd: number;
    pointsDelta: number;
  } | null>(null);
  const ghgInventoryLoadingRef = useRef<boolean>(false); // Prevent multiple simultaneous GHG Inventory loads
  const ghgInventoryLoadedForLocationRef = useRef<string | null>(null); // Track which location has GHG Inventory loaded
  const vmtChartDataLoadedRef = useRef<boolean>(false); // Track if VMT chart has loaded data for current location
  const overviewLoadStartRef = useRef<number | null>(null);
  const [overviewLoadElapsedSec, setOverviewLoadElapsedSec] = useState<number>(0);
  const TYPICAL_OVERVIEW_LOAD_SEC = 22;

  // Prevent Transportation and Waste sections from flickering: only show dynamic title parts after a short settle delay
  const [transportationSectionSettled, setTransportationSectionSettled] = useState(false);
  const [wasteSectionSettled, setWasteSectionSettled] = useState(false);
  const transportSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasteSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce helpers to avoid dozens of re-renders from chart callbacks firing repeatedly
  const SETTLE_MS = 600; // Defer mounting charts until parent has settled (stops chart flicker)
  const DEBOUNCE_MS = 200;
  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  // Lazy section mounts: mount all eagerly during initial load, then defer on revisit
  const eagerDuringLoad = !overviewFullyLoaded;
  const lazyBuildings = useLazySection('400px 0px', eagerDuringLoad);
  const lazyTransportation = useLazySection('400px 0px', eagerDuringLoad);
  const lazyWaste = useLazySection('400px 0px', eagerDuringLoad);
  const lazyPeopleEquity = useLazySection('400px 0px', eagerDuringLoad);
  const lazyGhg = useLazySection('400px 0px', eagerDuringLoad);

  // Scroll-driven dissolve: each section is a slide that fades in/out as you scroll
  const [sectionOpacities, setSectionOpacities] = useState<Record<string, number>>({});
  const sectionRefsMap = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const renderSampleTitle = (title: React.ReactNode, includeSample = true) => (
    <span>
      {includeSample && <span style={{ fontWeight: 400 }}>Sample of </span>}
      <span style={{ fontWeight: 700 }}>{title}</span>
      {includeSample && <span style={{ fontWeight: 400 }}> Figures.</span>}
    </span>
  );

  const isOverviewChartEnabled = React.useCallback((sectionId: string, chartId: string) => {
    // Do not enable any charts until settings are loaded (prevents loading transportation/energy/waste/people when they are disabled for the location)
    if (!overviewSettingsLoaded || overviewSettingsByChartId === null) return false;

    // Opt-in: chart must be explicitly enabled in DB. Undefined/missing = disabled.
    if (overviewSettingsByChartId[chartId] !== true) return false;

    const allowedRoles = overviewVisibilityByChartId?.[chartId];
    if (!allowedRoles || allowedRoles.length === 0) return true;
    if (hasRole('super_admin')) return true;

    const currentRole = user ? (userProfile?.role ?? 'user') : 'public';
    return allowedRoles.includes(currentRole);
  }, [overviewSettingsLoaded, overviewSettingsByChartId, overviewVisibilityByChartId, user, userProfile, hasRole]);

  const isOverviewSectionEnabled = React.useCallback((sectionId: string) => {
    const section = OVERVIEW_SECTIONS.find((item) => item.id === sectionId);
    if (!section) return true;
    return section.charts.some((chart) => isOverviewChartEnabled(sectionId, chart.id));
  }, [isOverviewChartEnabled]);

  const equityIndexSectionEnabled = isOverviewSectionEnabled('equity_index');
  const showLivabilityOverTimeChart = isOverviewChartEnabled('equity_index', 'livability_over_time');
  const showLivabilityRankingsChart = isOverviewChartEnabled('equity_index', 'livability_rankings_2023');
  /** Load ranking rows for the rankings table or to compute peer-rank percentile in the section title. */
  const shouldLoadLivabilityRankingRows = showLivabilityRankingsChart || equityIndexSectionEnabled;

  // Start settle timers for Transportation and Waste sections when location is selected (stops flicker)
  useEffect(() => {
    if (isLandingPage || !selectedLocation) {
      setTransportationSectionSettled(false);
      setWasteSectionSettled(false);
      if (transportSettleTimerRef.current) {
        clearTimeout(transportSettleTimerRef.current);
        transportSettleTimerRef.current = null;
      }
      if (wasteSettleTimerRef.current) {
        clearTimeout(wasteSettleTimerRef.current);
        wasteSettleTimerRef.current = null;
      }
      // Clear any pending debounce timers when location is cleared
      Object.keys(debounceTimersRef.current).forEach((key) => {
        const t = debounceTimersRef.current[key];
        if (t) clearTimeout(t);
      });
      debounceTimersRef.current = {};
      return;
    }
    setTransportationSectionSettled(false);
    setWasteSectionSettled(false);
    transportSettleTimerRef.current = setTimeout(() => {
      setTransportationSectionSettled(true);
      transportSettleTimerRef.current = null;
    }, SETTLE_MS);
    wasteSettleTimerRef.current = setTimeout(() => {
      setWasteSectionSettled(true);
      wasteSettleTimerRef.current = null;
    }, SETTLE_MS);
    return () => {
      if (transportSettleTimerRef.current) clearTimeout(transportSettleTimerRef.current);
      if (wasteSettleTimerRef.current) clearTimeout(wasteSettleTimerRef.current);
    };
  }, [selectedLocation, isLandingPage]);

  /** Rankings data and hero title use geography_type; keep it aligned with the selected place (city vs county). */
  useEffect(() => {
    if (!selectedLocation || availableLocations.length === 0) return;
    const rec = availableLocations.find((loc) => loc.name === selectedLocation);
    if (!rec) return;
    const want: 'City' | 'County' = rec.type === 'county' ? 'County' : 'City';
    setRankingGeographyType(want);
  }, [selectedLocation, availableLocations]);

  // Debounced metric updaters so chart callbacks don't cause a dozen re-renders
  const setVmtChangeDebounced = useDebouncedSetter(setVmtChangeSince2005, 'vmt', debounceTimersRef, DEBOUNCE_MS);
  const setPrivateVehicleDebounced = useDebouncedSetter(setPrivateVehiclePercent, 'privateVehicle', debounceTimersRef, DEBOUNCE_MS);
  const setEvPercentDebounced = useDebouncedSetter(setEvPercent, 'evPercent', debounceTimersRef, DEBOUNCE_MS);
  const setWasteChangeDebounced = useDebouncedSetter(setWasteChangeSince2005, 'wasteChange', debounceTimersRef, DEBOUNCE_MS);
  const setLandfillEmissionsDebounced = useDebouncedSetter(setLandfillEmissions2023, 'landfill', debounceTimersRef, DEBOUNCE_MS);

  useEffect(() => {
    if (isLandingPage || !selectedLocation) {
      setOverviewSettings(null);
      setOverviewSettingsLoaded(false);
      return;
    }

    const loadOverviewSettings = async () => {
      const selectWithRoles = 'section_id, chart_id, is_enabled, visible_roles';
      const selectBasic = 'section_id, chart_id, is_enabled';
      const globalWithRoles = await supabaseClient.from('admin_overview_settings').select(selectWithRoles).eq('location_name', GLOBAL_ADMIN_LOCATION);
      const localWithRoles = await supabaseClient.from('admin_overview_settings').select(selectWithRoles).eq('location_name', selectedLocation);

      const useFallback = (globalWithRoles.error && /visible_roles/i.test(globalWithRoles.error.message || '')) ||
        (localWithRoles.error && /visible_roles/i.test(localWithRoles.error.message || ''));
      const globalRes = useFallback
        ? await supabaseClient.from('admin_overview_settings').select(selectBasic).eq('location_name', GLOBAL_ADMIN_LOCATION)
        : globalWithRoles;
      const localRes = useFallback
        ? await supabaseClient.from('admin_overview_settings').select(selectBasic).eq('location_name', selectedLocation)
        : localWithRoles;

      const globalError = globalRes.error;
      const localError = localRes.error;

      if (globalError) {
        console.error('Overview: Failed to load global admin settings:', globalError);
      }
      if (localError) {
        console.error('Overview: Failed to load local admin settings:', localError);
      }

      if (globalError && localError) {
        setOverviewSettings(null);
        setOverviewSettingsByChartId({});
        setOverviewVisibilityByChartId(null);
      } else {
        const nextSettings: Record<string, Record<string, boolean>> = {};
        const nextByChartId: Record<string, boolean> = {};
        const nextVisibilityByChartId: Record<string, string[] | null> = {};
        const globalRows = globalRes.data || [];
        const localRows = localRes.data || [];
        // Process global first
        globalRows.forEach((row: any) => {
          if (!nextSettings[row.section_id]) nextSettings[row.section_id] = {};
          nextSettings[row.section_id][row.chart_id] = row.is_enabled;
          nextByChartId[row.chart_id] = row.is_enabled;
          nextVisibilityByChartId[row.chart_id] = Array.isArray(row.visible_roles) ? row.visible_roles : null;
        });
        // Local overrides global; if multiple local rows exist for same chart_id, disabled (false) wins
        localRows.forEach((row: any) => {
          if (!nextSettings[row.section_id]) nextSettings[row.section_id] = {};
          nextSettings[row.section_id][row.chart_id] = row.is_enabled;
          const prev = nextByChartId[row.chart_id];
          nextByChartId[row.chart_id] = prev === false || row.is_enabled === false ? false : row.is_enabled;
          nextVisibilityByChartId[row.chart_id] = Array.isArray(row.visible_roles) ? row.visible_roles : null;
        });
        setOverviewSettings(nextSettings);
        setOverviewSettingsByChartId(nextByChartId);
        setOverviewVisibilityByChartId(nextVisibilityByChartId);
      }
      setOverviewSettingsLoaded(true);
    };

    loadOverviewSettings();
  }, [isLandingPage, selectedLocation]);

  // Climate Equity Over Time: fetch trend data from livability_index_precalculated
  useEffect(() => {
    if (!showLivabilityOverTimeChart || !selectedLocation) {
      setLivabilityTrendRows([]);
      setLivabilityTrendLoading(false);
      setLivabilityTrendError(null);
      return;
    }
    const selectedLocationRecord = availableLocations.find((loc) => loc.name === selectedLocation);
    const locationType = selectedLocationRecord?.type === 'county' ? 'County' : 'City';
    const cacheKey = `${selectedLocation}|${locationType}`;
    const cachedRows = livabilityTrendCacheRef.current[cacheKey];
    if (cachedRows && cachedRows.length > 0) {
      setLivabilityTrendRows(cachedRows);
      setLivabilityTrendLoading(false);
      setLivabilityTrendError(null);
      return;
    }
    let isActive = true;
    const loadTrend = async () => {
      setLivabilityTrendLoading(true);
      setLivabilityTrendError(null);
      try {
        const { data, error } = await supabaseClient
          .from('livability_index_precalculated')
          .select('year,total_score_percentile,total_burdens_percentile,total_benefits_percentile,ldv_emissions_percentile,hdv_emissions_percentile,elec_total_emissions_percentile,ng_total_emissions_percentile,waste_emissions_percentile,offroad_emissions_percentile,diesel_pm_percentile,pm2_5_percentile,ozone_percentile,tox_release_percentile,haz_waste_percentile,asthma_percentile,cardiovascular_disease_percentile,low_birth_weight_percentile,traffic_percentile,commute_time_percentile,transportation_cost_burden_percentile,housing_burden_renter_percentile,housing_burden_owner_percentile,housing_burden_percentile,percent_fossil_heating_percentile,income_equity_percentile,education_equity_percentile,wage_equity_percentile,median_household_income_percentile,employment_rate_percentile,job_wage_percentile,educational_attainment_percentile,health_insurance_percentile,ev_per_1000_people_percentile,ev_charging_percentile,public_transit_percentile,solar_pv_percentile,heat_pump_percentile,benefit_community_resources_percentile')
          .eq('location_name', selectedLocation)
          .eq('geography_type', locationType)
          .gte('year', 2015)
          .lte('year', 2023)
          .order('year', { ascending: true })
          .range(0, 200);
        if (error) throw error;
        if (!isActive) return;
        const rowsRaw = (data || []) as Array<Record<string, any>>;
        const byYear = new Map<number, LivabilityTrendRawRow>();
        // Fetch displacement risk by year for this location
        const displacementGeoType = locationType === 'County' ? 'county' : 'place';
        let drData: Array<{ year: number; percentile: number }> | null = null;
        const { data: drPlaceCounty } = await supabaseClient
          .from('displacement_risk')
          .select('year, percentile')
          .eq('location_name', selectedLocation)
          .eq('geography_type', displacementGeoType)
          .gte('year', 2015)
          .lte('year', 2023)
          .not('percentile', 'is', null);
        drData = (drPlaceCounty || []) as Array<{ year: number; percentile: number }>;
        if (drData.length === 0 && locationType === 'City') {
          const countyName = supabaseLocationService.getCountyForCity(selectedLocation);
          if (countyName) {
            const { data: drCounty } = await supabaseClient
              .from('displacement_risk')
              .select('year, percentile')
              .eq('location_name', countyName)
              .eq('geography_type', 'county')
              .gte('year', 2015)
              .lte('year', 2023)
              .not('percentile', 'is', null);
            drData = (drCounty || []) as Array<{ year: number; percentile: number }>;
          }
        }
        const displacementByYear = new Map<number, number>();
        (drData || []).forEach((r: { year: number; percentile: number }) => {
          const y = Number(r.year);
          const pct = r.percentile != null && Number.isFinite(Number(r.percentile)) ? Number(r.percentile) : null;
          if (Number.isFinite(y) && pct != null) displacementByYear.set(y, normalizePercentile(pct));
        });
        rowsRaw.forEach((row) => {
          const year = Number(row.year);
          if (!Number.isFinite(year)) return;
          const candidate: LivabilityTrendRawRow = {
            year,
            total_score_percentile: num(row.total_score_percentile),
            total_burdens_percentile: num(row.total_burdens_percentile),
            total_benefits_percentile: num(row.total_benefits_percentile),
            ldv_emissions_percentile: num(row.ldv_emissions_percentile),
            hdv_emissions_percentile: num(row.hdv_emissions_percentile),
            elec_total_emissions_percentile: num(row.elec_total_emissions_percentile),
            ng_total_emissions_percentile: num(row.ng_total_emissions_percentile),
            waste_emissions_percentile: num(row.waste_emissions_percentile),
            offroad_emissions_percentile: num(row.offroad_emissions_percentile),
            diesel_pm_percentile: num(row.diesel_pm_percentile),
            pm2_5_percentile: num(row.pm2_5_percentile),
            ozone_percentile: num(row.ozone_percentile),
            tox_release_percentile: num(row.tox_release_percentile),
            haz_waste_percentile: num(row.haz_waste_percentile),
            asthma_percentile: num(row.asthma_percentile),
            cardiovascular_disease_percentile: num(row.cardiovascular_disease_percentile),
            low_birth_weight_percentile: num(row.low_birth_weight_percentile),
            traffic_percentile: num(row.traffic_percentile),
            commute_time_percentile: num(row.commute_time_percentile),
            transportation_cost_burden_percentile: num(row.transportation_cost_burden_percentile),
            housing_burden_renter_percentile: num(row.housing_burden_renter_percentile),
            housing_burden_owner_percentile: num(row.housing_burden_owner_percentile),
            housing_burden_percentile: num(row.housing_burden_percentile),
            percent_fossil_heating_percentile: num(row.percent_fossil_heating_percentile),
            income_equity_percentile: num(row.income_equity_percentile),
            education_equity_percentile: num(row.education_equity_percentile),
            wage_equity_percentile: num(row.wage_equity_percentile),
            median_household_income_percentile: num(row.median_household_income_percentile),
            employment_rate_percentile: num(row.employment_rate_percentile),
            job_wage_percentile: num(row.job_wage_percentile),
            educational_attainment_percentile: num(row.educational_attainment_percentile),
            health_insurance_percentile: num(row.health_insurance_percentile),
            ev_per_1000_people_percentile: num(row.ev_per_1000_people_percentile),
            ev_charging_percentile: num(row.ev_charging_percentile),
            public_transit_percentile: num(row.public_transit_percentile),
            solar_pv_percentile: num(row.solar_pv_percentile),
            heat_pump_percentile: num(row.heat_pump_percentile),
            tree_canopy_percentile: num(row.benefit_community_resources_percentile),
            benefit_community_resources_percentile: num(row.benefit_community_resources_percentile),
            displacement_risk_percentile: displacementByYear.has(year) ? displacementByYear.get(year)! : null
          };
          const existing = byYear.get(year);
          if (!existing) {
            byYear.set(year, candidate);
            return;
          }
          const existingCount = Object.values(existing).filter((v) => typeof v === 'number' && Number.isFinite(v)).length;
          const candidateCount = Object.values(candidate).filter((v) => typeof v === 'number' && Number.isFinite(v)).length;
          if (candidateCount > existingCount) byYear.set(year, candidate);
        });
        // Restrict to 2015–2023 only
        const cleaned = Array.from(byYear.values())
          .filter((r) => r.year >= 2015 && r.year <= 2023)
          .sort((a, b) => a.year - b.year);
        livabilityTrendCacheRef.current[cacheKey] = cleaned;
        setLivabilityTrendRows(cleaned);
      } catch (err) {
        console.error('Overview: Failed to load climate equity trend data:', err);
        if (!isActive) return;
        setLivabilityTrendRows([]);
        setLivabilityTrendError('Unable to load climate equity trend data right now.');
      } finally {
        if (isActive) setLivabilityTrendLoading(false);
      }
    };
    loadTrend();
    return () => { isActive = false; };
  }, [showLivabilityOverTimeChart, selectedLocation, availableLocations]);

  // Transportation cost burden + employment rate for BurdenBenefitsChart: same source as Climate Equity Over Time.
  // When the over-time chart is disabled, livabilityTrendRows stays empty — still fetch latest row (City/County) here.
  useEffect(() => {
    if (!selectedLocation) {
      setTransportationCostBurdenLivabilityPercentile(null);
      setEmploymentRateLivabilityPercentile(null);
      return;
    }
    let cancelled = false;
    const loc = selectedLocation.trim();
    const selectedLocationRecord = availableLocations.find((l) => l.name === selectedLocation);
    const locationType: 'City' | 'County' = selectedLocationRecord?.type === 'county' ? 'County' : 'City';

    const applyRow = (row: {
      transportation_cost_burden_percentile?: unknown;
      employment_rate_percentile?: unknown;
    }): boolean => {
      const tcb = row.transportation_cost_burden_percentile;
      const er = row.employment_rate_percentile;
      if (tcb != null && Number.isFinite(Number(tcb))) {
        setTransportationCostBurdenLivabilityPercentile(Number(tcb));
      } else {
        setTransportationCostBurdenLivabilityPercentile(null);
      }
      if (er != null && Number.isFinite(Number(er))) {
        setEmploymentRateLivabilityPercentile(Number(er));
        return true;
      }
      setEmploymentRateLivabilityPercentile(null);
      return false;
    };

    (async () => {
      const rows = livabilityTrendRows.filter((r) => r.year <= 2023);
      if (rows.length > 0) {
        const sorted = [...rows].sort((a, b) => b.year - a.year);
        const row = sorted[0];
        const hasEmployment = applyRow(row);
        if (!cancelled && !hasEmployment) {
          try {
            const geo = await supabaseLocationService.getGeofipsWithGeographyType(selectedLocation);
            const clientPct = await fetchEmploymentRateBenefitPercentile(
              selectedLocation,
              selectedGeofips ?? null,
              geo?.geography_type ?? null
            );
            if (!cancelled && clientPct != null) setEmploymentRateLivabilityPercentile(clientPct);
          } catch {
            if (!cancelled) setEmploymentRateLivabilityPercentile(null);
          }
        }
        return;
      }

      const tryTypes: Array<'City' | 'County' | 'Place'> =
        locationType === 'County' ? ['County'] : ['City', 'Place'];

      let snapshot: {
        transportation_cost_burden_percentile?: unknown;
        employment_rate_percentile?: unknown;
      } | null = null;

      for (const gt of tryTypes) {
        if (cancelled) return;
        const { data, error } = await supabaseClient
          .from('livability_index_precalculated')
          .select('transportation_cost_burden_percentile, employment_rate_percentile, year')
          .eq('location_name', loc)
          .eq('geography_type', gt)
          .lte('year', 2023)
          .order('year', { ascending: false })
          .limit(1);
        if (error) continue;
        const row = data?.[0];
        if (row && (row.transportation_cost_burden_percentile != null || row.employment_rate_percentile != null)) {
          snapshot = row;
          break;
        }
      }

      if (!snapshot && locationType === 'City' && !cancelled) {
        const { data, error } = await supabaseClient
          .from('livability_index_precalculated')
          .select('transportation_cost_burden_percentile, employment_rate_percentile, year')
          .ilike('location_name', `${loc}%`)
          .eq('geography_type', 'City')
          .lte('year', 2023)
          .order('year', { ascending: false })
          .limit(1);
        if (!error && data?.[0]) snapshot = data[0];
      }

      if (cancelled) return;
      if (snapshot) {
        const hasEmployment = applyRow(snapshot);
        if (!hasEmployment) {
          try {
            const geo = await supabaseLocationService.getGeofipsWithGeographyType(selectedLocation);
            const clientPct = await fetchEmploymentRateBenefitPercentile(
              selectedLocation,
              selectedGeofips ?? null,
              geo?.geography_type ?? null
            );
            if (!cancelled && clientPct != null) setEmploymentRateLivabilityPercentile(clientPct);
          } catch {
            if (!cancelled) setEmploymentRateLivabilityPercentile(null);
          }
        }
      } else {
        setTransportationCostBurdenLivabilityPercentile(null);
        try {
          const geo = await supabaseLocationService.getGeofipsWithGeographyType(selectedLocation);
          const clientPct = await fetchEmploymentRateBenefitPercentile(
            selectedLocation,
            selectedGeofips ?? null,
            geo?.geography_type ?? null
          );
          if (!cancelled) setEmploymentRateLivabilityPercentile(clientPct ?? null);
        } catch {
          if (!cancelled) setEmploymentRateLivabilityPercentile(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedLocation,
    selectedGeofips,
    availableLocations,
    livabilityTrendRows,
  ]);

  // Public transit benefit percentile for BurdenBenefitsChart (livability row or ACS fallback).
  useEffect(() => {
    if (!selectedLocation) {
      setPublicTransitLivabilityPercentile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const rows = livabilityTrendRows.filter((r) => r.year <= 2023);
      if (rows.length > 0) {
        const sorted = [...rows].sort((a, b) => b.year - a.year);
        const pt = sorted[0]?.public_transit_percentile;
        if (pt != null && Number.isFinite(Number(pt))) {
          if (!cancelled) setPublicTransitLivabilityPercentile(Number(pt));
          return;
        }
      }
      try {
        const result = await fetchPublicTransitLivabilityPercentile(
          selectedLocation,
          selectedGeofips ?? null
        );
        if (!cancelled) setPublicTransitLivabilityPercentile(result?.percentile ?? null);
      } catch {
        if (!cancelled) setPublicTransitLivabilityPercentile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, selectedGeofips, livabilityTrendRows]);

  // Fossil heating percentile for the equity burdens card: precalculated index table first, then client peer rank from house_heating_fuel.
  useEffect(() => {
    if (!selectedLocation) {
      setFossilHeatingLivabilityPercentile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const loc = selectedLocation.trim();
      const selectedLocationRecord = availableLocations.find((l) => l.name === selectedLocation);
      const primaryType: 'City' | 'County' = selectedLocationRecord?.type === 'county' ? 'County' : 'City';
      const geoTypes =
        primaryType === 'County'
          ? ['County']
          : Array.from(new Set(['City', 'Place', primaryType].filter(Boolean)));
      const toNum = (raw: unknown): number | null => {
        if (raw == null || !Number.isFinite(Number(raw))) return null;
        return Number(raw);
      };
      const tryLivabilityRow = async (year: number, gt: string, usePrefixName: boolean) => {
        let q = supabaseClient
          .from('livability_index_precalculated')
          .select('percent_fossil_heating_percentile')
          .eq('geography_type', gt)
          .eq('year', year)
          .limit(1);
        q = usePrefixName ? q.ilike('location_name', `${loc}%`) : q.eq('location_name', loc);
        const { data, error } = await q;
        if (error) return null;
        return toNum(data?.[0]?.percent_fossil_heating_percentile);
      };
      for (const y of [2023, 2022, 2021, 2020]) {
        if (cancelled) return;
        for (const gt of geoTypes) {
          if (cancelled) return;
          let v = await tryLivabilityRow(y, gt, false);
          if (v == null) v = await tryLivabilityRow(y, gt, true);
          if (v != null) {
            if (!cancelled) setFossilHeatingLivabilityPercentile(v);
            return;
          }
        }
      }
      try {
        const clientPct = await fetchFossilHeatingBurdenPercentile(selectedLocation, null, null);
        if (!cancelled && clientPct != null) {
          setFossilHeatingLivabilityPercentile(clientPct);
          return;
        }
      } catch {
        /* keep null */
      }
      if (!cancelled) setFossilHeatingLivabilityPercentile(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, availableLocations]);

  // Sync Housing Burden percentiles for BurdenBenefitsChart from livability_index_precalculated
  // so they match the Climate Equity Over Time chart (same source: livability_index_precalculated)
  useEffect(() => {
    if (!selectedLocation) return;

    const applyFromRow = (row: {
      housing_burden_percentile?: number | null;
      housing_burden_renter_percentile?: number | null;
      housing_burden_owner_percentile?: number | null;
    }) => {
      const pct = row.housing_burden_percentile ?? null;
      const renterPct = row.housing_burden_renter_percentile ?? null;
      const ownerPct = row.housing_burden_owner_percentile ?? null;
      if (pct != null && Number.isFinite(pct)) setHousingBurdenPercentile(pct);
      if (renterPct != null && Number.isFinite(renterPct)) setHousingBurdenRenterPercentile(renterPct);
      if (ownerPct != null && Number.isFinite(ownerPct)) setHousingBurdenOwnerPercentile(ownerPct);
    };

    if (livabilityTrendRows.length > 0) {
      const row = livabilityTrendRows.find((r) => r.year === 2023) ?? livabilityTrendRows[livabilityTrendRows.length - 1];
      applyFromRow(row);
      return;
    }

    let cancelled = false;
    (async () => {
      const loc = selectedLocation.trim();
      const selectedLocationRecord = availableLocations.find((l) => l.name === selectedLocation);
      const geoTypes: Array<'City' | 'County' | 'Place'> =
        selectedLocationRecord?.type === 'county' ? ['County'] : ['City', 'Place'];
      for (const gt of geoTypes) {
        if (cancelled) return;
        const { data, error } = await supabaseClient
          .from('livability_index_precalculated')
          .select(
            'housing_burden_percentile, housing_burden_renter_percentile, housing_burden_owner_percentile, year'
          )
          .eq('location_name', loc)
          .eq('geography_type', gt)
          .eq('year', 2023)
          .limit(1);
        if (!error && data?.[0]) {
          applyFromRow(data[0] as LivabilityTrendRawRow);
          return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, livabilityTrendRows, availableLocations]);

  // Climate Equity Over Time: compute ranking change from 2015 to 2023 (needs full ranking data)
  useEffect(() => {
    if (!showLivabilityOverTimeChart || !selectedLocation || livabilityTrendRows.length < 2) {
      setLivabilityRankingChangeFrom2005(null);
      return;
    }
    const locationRecord = availableLocations.find((loc) => loc.name === selectedLocation);
    const locationType: 'City' | 'County' = locationRecord?.type === 'county' ? 'County' : 'City';
    const startYear = 2015;
    const endYear = 2023;
    const trendYears = livabilityTrendRows.map((r) => r.year);
    const hasStartYear = trendYears.includes(startYear);
    const effectiveStartYear = hasStartYear ? startYear : Math.min(...trendYears.filter((y) => y <= 2023));

    const loadAndComputeRankingChange = async () => {
      let rows = rankingDataCacheRef.current[locationType];
      if (!rows || rows.length === 0) {
        try {
          const allRows = await fetchAllPages<LivabilityRankingSourceRow>(
            (from, to) => supabaseClient
              .from('livability_index_precalculated')
              .select('location_name, geofips, geography_type, year, total_score_percentile, total_burdens_percentile, total_benefits_percentile')
              .eq('geography_type', locationType)
              .not('total_burdens_percentile', 'is', null)
              .not('total_benefits_percentile', 'is', null)
              .order('year', { ascending: false })
              .order('geofips', { ascending: true })
              .range(from, to)
          );
          rankingDataCacheRef.current[locationType] = allRows;
          rows = allRows;
        } catch (err) {
          console.error('Overview: Failed to load ranking-by-year data:', err);
          setLivabilityRankingChangeFrom2005(null);
          return;
        }
      }
      const score = (r: LivabilityRankingSourceRow) => {
        const s = overallLivabilityPercentileScore(r);
        return s != null && Number.isFinite(s) ? s : -1;
      };
      const getRankForYear = (year: number): number | null => {
        const yearRows = (rows as LivabilityRankingSourceRow[]).filter((r) => Number(r.year) === year);
        const deduped = new Map<string, { name: string; score: number }>();
        yearRows.forEach((r) => {
          const g = (r.geofips || '').trim();
          const n = (r.location_name || '').trim();
          const s = score(r);
          if (!g || !n || s < 0) return;
          const ex = deduped.get(g);
          if (!ex || s > ex.score) deduped.set(g, { name: n, score: s });
        });
        const sorted = Array.from(deduped.values()).sort((a, b) => b.score - a.score);
        const idx = sorted.findIndex((r) => r.name.toLowerCase() === selectedLocation.toLowerCase());
        return idx >= 0 ? idx + 1 : null;
      };
      const effectiveEndYear = trendYears.includes(endYear) ? endYear : Math.max(...trendYears.filter((y) => y <= 2023));
      const rankStart = getRankForYear(effectiveStartYear);
      const rankEnd = getRankForYear(effectiveEndYear);
      const scoreFromRow = (r: LivabilityTrendRawRow) => overallLivabilityPercentileScore(r);
      const rowStart = livabilityTrendRows.find((r) => r.year === effectiveStartYear);
      const rowEnd = livabilityTrendRows.find((r) => r.year === effectiveEndYear);
      const pointsStart = rowStart ? scoreFromRow(rowStart) : null;
      const pointsEnd = rowEnd ? scoreFromRow(rowEnd) : null;
      if (rankStart != null && rankEnd != null && pointsStart != null && pointsEnd != null) {
        const pStart = displayPercentile(pointsStart) ?? pointsStart;
        const pEnd = displayPercentile(pointsEnd) ?? pointsEnd;
        setLivabilityRankingChangeFrom2005({
          rankStart,
          rankEnd,
          startYear: effectiveStartYear,
          endYear: effectiveEndYear,
          rankDelta: rankStart - rankEnd,
          pointsStart: pStart,
          pointsEnd: pEnd,
          pointsDelta: Math.round((pEnd - pStart) * 10) / 10
        });
      } else {
        setLivabilityRankingChangeFrom2005(null);
      }
    };
    loadAndComputeRankingChange();
  }, [showLivabilityOverTimeChart, selectedLocation, livabilityTrendRows, availableLocations]);

  // Climate Equity Rankings: fetch from livability_index_precalculated (same source as Climate Equity Over Time).
  // Overall view: total_score_percentile when present, else (Benefits + (100 − Burdens)) / 2.
  useEffect(() => {
    if (!overviewSettingsLoaded || !shouldLoadLivabilityRankingRows) {
      setRankingDisplayRows([]);
      setRankingDisplayCenteredOnSelection(false);
      setRankingTotalRows(0);
      setRankingByLocation(new Map());
      setRankingScoreByLocation(new Map());
      setOverallPeerRankMeta(null);
      setRankingError(null);
      setRankingLoading(false);
      return;
    }
    let isActive = true;
    const calculateAndSetRankings = (rows: LivabilityRankingSourceRow[]) => {
      const getScoreForRow = (r: LivabilityRankingSourceRow): number | null => {
        if (rankingView === 'overall') return overallLivabilityPercentileScore(r);
        if (rankingView === 'burdens') {
          const cats = LIVABILITY_BURDEN_CATEGORIES;
          if (rankingCategory === 'all') {
            const catScores = cats.map((cat) => {
              const vals = cat.sub.map((s) => num(r[s.key]));
              const a = avg(vals);
              return a != null ? 100 - a : null;
            });
            const s = avg(catScores);
            return s;
          }
          const cat = cats.find((c) => c.id === rankingCategory);
          if (!cat) return null;
          if (rankingSubcategory) {
            const sub = cat.sub.find((s) => s.key === rankingSubcategory);
            if (!sub) return null;
            const v = num(r[sub.key]);
            return v != null ? 100 - v : null;
          }
          const vals = cat.sub.map((s) => num(r[s.key]));
          const a = avg(vals);
          return a != null ? 100 - a : null;
        }
        if (rankingView === 'benefits') {
          const cats = LIVABILITY_BENEFIT_CATEGORIES;
          if (rankingCategory === 'all') {
            const catScores = cats.map((cat) => {
              const vals = cat.sub.map((s) => num(r[s.key]));
              return avg(vals);
            });
            return avg(catScores);
          }
          const cat = cats.find((c) => c.id === rankingCategory);
          if (!cat) return null;
          if (rankingSubcategory) {
            const sub = cat.sub.find((s) => s.key === rankingSubcategory);
            if (!sub) return null;
            const v = num(r[sub.key]);
            return v;
          }
          const vals = cat.sub.map((s) => num(r[s.key]));
          return avg(vals);
        }
        return null;
      };
      const getScoreWithFallback = (r: LivabilityRankingSourceRow): number | null => {
        const s = getScoreForRow(r);
        if (s != null && Number.isFinite(s)) return s;
        return overallLivabilityPercentileScore(r);
      };
      const yearCounts = new Map<number, number>();
      rows.forEach((row) => {
        const y = Number(row.year);
        if (!Number.isFinite(y)) return;
        yearCounts.set(y, (yearCounts.get(y) || 0) + 1);
      });
      const availableYears = Array.from(yearCounts.keys()).sort((a, b) => b - a);
      setRankingAvailableYears(availableYears);
      if (availableYears.length === 0) {
        setRankingDisplayRows([]);
        setRankingDisplayCenteredOnSelection(false);
        setRankingTotalRows(0);
        setRankingByLocation(new Map());
        setRankingScoreByLocation(new Map());
        setOverallPeerRankMeta(null);
        setRankingError(`No viable ${rankingGeographyType.toLowerCase()} ranking data available.`);
        return;
      }
      let effectiveYear = rankingYear;
      if (!availableYears.includes(effectiveYear)) {
        effectiveYear = availableYears.includes(2023) ? 2023 : availableYears[0];
        setRankingYear(effectiveYear);
      }
      const dedupOverall = new Map<string, { locationName: string; score: number }>();
      rows.forEach((row) => {
        if (Number(row.year) !== effectiveYear) return;
        const locationName = (row.location_name || '').trim();
        const geofips = (row.geofips || '').trim();
        if (!locationName || !geofips) return;
        const score = overallLivabilityPercentileScore(row);
        if (score == null || !Number.isFinite(score)) return;
        const existing = dedupOverall.get(geofips);
        const candidate = { locationName, score };
        if (!existing || candidate.score > existing.score) dedupOverall.set(geofips, candidate);
      });
      const sortedOverall = Array.from(dedupOverall.values()).sort((a, b) => b.score - a.score);
      const overallRankByLoc = new Map<string, number>();
      sortedOverall.forEach((row, idx) => overallRankByLoc.set(row.locationName.toLowerCase(), idx + 1));
      setOverallPeerRankMeta({ n: sortedOverall.length, rankByLocation: overallRankByLoc });

      const dedupedByLocation = new Map<string, LivabilityRankingRow>();
      rows.forEach((row) => {
        if (Number(row.year) !== effectiveYear) return;
        const locationName = (row.location_name || '').trim();
        const geofips = (row.geofips || '').trim();
        const burdens = num(row.total_burdens_percentile);
        const benefits = num(row.total_benefits_percentile);
        if (!locationName || !geofips) return;
        const score = (rankingView === 'overall' ? getScoreForRow : getScoreWithFallback)(row);
        if (score == null || !Number.isFinite(score)) return;
        const key = geofips;
        const candidate: LivabilityRankingRow = { rank: 0, locationName, geofips, score, burdens: burdens ?? 0, benefits: benefits ?? 0 };
        const existing = dedupedByLocation.get(key);
        if (!existing || candidate.score > existing.score) {
          dedupedByLocation.set(key, candidate);
        }
      });
      const sorted = Array.from(dedupedByLocation.values()).sort((a, b) => b.score - a.score);
      setRankingTotalRows(sorted.length);
      const byLocation = new Map<string, number>();
      const scoreByLocation = new Map<string, number>();
      sorted.forEach((row, idx) => {
        const key = row.locationName.toLowerCase();
        byLocation.set(key, idx + 1);
        scoreByLocation.set(key, row.score);
      });
      setRankingByLocation(byLocation);
      setRankingScoreByLocation(scoreByLocation);
      let display: LivabilityRankingRow[] = [];
      let centered = false;
      if (rankingRowCount === 'all') {
        display = sorted.map((row, idx) => ({ ...row, rank: idx + 1 }));
      } else {
        const n = Math.min(rankingRowCount, sorted.length);
        const selectedKey = selectedLocation?.trim().toLowerCase();
        const userIdx = selectedKey ? sorted.findIndex((r) => r.locationName.toLowerCase() === selectedKey) : -1;
        if (selectedKey && userIdx >= 0 && n > 0) {
          centered = true;
          const midOffset = Math.ceil(n / 2) - 1;
          let start = userIdx - midOffset;
          if (start < 0) start = 0;
          if (start + n > sorted.length) start = Math.max(0, sorted.length - n);
          const windowSlice = sorted.slice(start, start + n);
          display = windowSlice.map((row, i) => ({ ...row, rank: start + i + 1 }));
        } else {
          display = sorted.slice(0, n).map((row, idx) => ({ ...row, rank: idx + 1 }));
        }
      }
      setRankingDisplayCenteredOnSelection(centered);
      setRankingDisplayRows(display);
      setRankingError(null);
    };
    const loadLivabilityRankings = async () => {
      const cachedRows = rankingDataCacheRef.current[rankingGeographyType];
      if (cachedRows && cachedRows.length > 0) {
        calculateAndSetRankings(cachedRows);
        setRankingLoading(false);
        return;
      }
      setRankingLoading(true);
      setRankingError(null);
      try {
        const rankingSelectCols = 'location_name,geofips,geography_type,year,total_score_percentile,total_burdens_percentile,total_benefits_percentile,ldv_emissions_percentile,hdv_emissions_percentile,elec_total_emissions_percentile,ng_total_emissions_percentile,waste_emissions_percentile,offroad_emissions_percentile,diesel_pm_percentile,pm2_5_percentile,ozone_percentile,tox_release_percentile,haz_waste_percentile,asthma_percentile,cardiovascular_disease_percentile,low_birth_weight_percentile,traffic_percentile,commute_time_percentile,transportation_cost_burden_percentile,housing_burden_renter_percentile,housing_burden_owner_percentile,housing_burden_percentile,percent_fossil_heating_percentile,income_equity_percentile,education_equity_percentile,wage_equity_percentile,median_household_income_percentile,employment_rate_percentile,job_wage_percentile,educational_attainment_percentile,health_insurance_percentile,ev_per_1000_people_percentile,ev_charging_percentile,public_transit_percentile,solar_pv_percentile,heat_pump_percentile,tree_canopy_percentile,benefit_community_resources_percentile';
        const allRows = await fetchAllPages<LivabilityRankingSourceRow>(
          (from, to) => supabaseClient
            .from('livability_index_precalculated')
            .select(rankingSelectCols)
            .eq('geography_type', rankingGeographyType)
            .order('year', { ascending: false })
            .order('geofips', { ascending: true })
            .range(from, to)
        );
        if (!isActive) return;
        rankingDataCacheRef.current[rankingGeographyType] = allRows;
        calculateAndSetRankings(allRows);
      } catch (err) {
        console.error('Overview: Failed to load climate equity rankings:', err);
        if (!isActive) return;
        setRankingDisplayRows([]);
        setRankingDisplayCenteredOnSelection(false);
        setRankingTotalRows(0);
        setRankingByLocation(new Map());
        setRankingScoreByLocation(new Map());
        setOverallPeerRankMeta(null);
        setRankingError('Unable to load rankings right now.');
      } finally {
        if (isActive) setRankingLoading(false);
      }
    };
    loadLivabilityRankings();
    return () => { isActive = false; };
  }, [overviewSettingsLoaded, shouldLoadLivabilityRankingRows, rankingGeographyType, rankingYear, rankingRowCount, rankingView, rankingCategory, rankingSubcategory, selectedLocation]);

  const livabilityTrendFigure = React.useMemo(() => {
    const rows = livabilityTrendRows.filter((r) => r.year >= 2015 && r.year <= 2023);
    if (!rows.length) return null;
    const years = rows.map((row) => row.year);
    const burdenCategorySeries: Record<string, number | null>[] = years.map((_, i) => {
      const r = rows[i];
      if (!r) return { ghg: null, pollution: null, health: null, transportation: null, built_env: null };
      return {
        ghg: displayPercentile(avg([r.ldv_emissions_percentile, r.hdv_emissions_percentile, r.elec_total_emissions_percentile, r.ng_total_emissions_percentile, r.waste_emissions_percentile, r.offroad_emissions_percentile])) ?? null,
        pollution: displayPercentile(avg([r.diesel_pm_percentile, r.pm2_5_percentile, r.ozone_percentile, r.tox_release_percentile, r.haz_waste_percentile])) ?? null,
        health: displayPercentile(avg([r.asthma_percentile, r.cardiovascular_disease_percentile, r.low_birth_weight_percentile])) ?? null,
        transportation: displayPercentile(
          avg(
            [r.traffic_percentile, r.commute_time_percentile, r.transportation_cost_burden_percentile].filter(
              (v): v is number => v != null && Number.isFinite(v)
            )
          )
        ) ?? null,
        built_env: (() => {
          const hbComp = r.housing_burden_percentile ?? avg([r.housing_burden_renter_percentile, r.housing_burden_owner_percentile]);
          const parts = [hbComp, r.displacement_risk_percentile, r.percent_fossil_heating_percentile].filter(
            (v): v is number => v != null && Number.isFinite(v)
          );
          return displayPercentile(parts.length ? avg(parts) : null) ?? null;
        })()
      };
    });
    const benefitCategorySeries: Record<string, number | null>[] = years.map((_, i) => {
      const r = rows[i];
      if (!r) return { equity: null, socioeconomic: null, mobility: null, built_env: null, community_resources: null };
      return {
        equity: displayPercentile(avg([r.income_equity_percentile, r.education_equity_percentile, r.wage_equity_percentile])) ?? null,
        socioeconomic: displayPercentile(avg([r.median_household_income_percentile, r.employment_rate_percentile, r.educational_attainment_percentile, r.health_insurance_percentile, r.job_wage_percentile])) ?? null,
        mobility: displayPercentile(avg([r.public_transit_percentile, r.ev_per_1000_people_percentile, r.ev_charging_percentile])) ?? null,
        built_env: displayPercentile(avg([r.solar_pv_percentile, r.heat_pump_percentile])) ?? null,
        community_resources: displayPercentile(r.tree_canopy_percentile ?? r.benefit_community_resources_percentile) ?? null
      };
    });
    const BURDEN_CATEGORIES = LIVABILITY_BURDEN_CATEGORIES;
    const BENEFIT_CATEGORIES = LIVABILITY_BENEFIT_CATEGORIES;
    const colors = {
      overall: ['#1d4ed8', '#059669', '#dc2626'],
      burdens: ['#b91c1c', '#c2410c', '#a16207', '#4d7c0f', '#15803d'],
      benefits: ['#047857', '#0d9488', '#0891b2', '#0284c7', '#2563eb'],
      builtEnvBurdens: ['#0284c7', '#7c3aed', '#0d9488', '#b45309'] // Renter, Owner, Displacement, Fossil heating
    };
    const livabilityIndexSeries = rows.map((row) => {
      const s = overallLivabilityPercentileScore(row);
      return s != null ? displayPercentile(s) : null;
    });
    let chartData: { labels: number[]; datasets: { label: string; data: (number | null)[]; borderColor: string; backgroundColor: string; borderWidth: number; borderDash?: number[]; tension: number }[] };
    if (livabilityTrendView === 'overall') {
      chartData = {
        labels: years,
        datasets: [
          { label: 'Total score (percentile)', data: livabilityIndexSeries, borderColor: colors.overall[0], backgroundColor: 'transparent', borderWidth: 3, tension: 0.25 }
        ]
      };
    } else if (livabilityTrendView === 'burdens') {
      if (livabilityTrendCategory === 'all') {
        chartData = { labels: years, datasets: BURDEN_CATEGORIES.map((cat, i) => ({ label: cat.label, data: burdenCategorySeries.map((s) => s[cat.id] ?? null), borderColor: colors.burdens[i % colors.burdens.length], backgroundColor: 'transparent', borderWidth: 2, tension: 0.25 })) };
      } else {
        const cat = BURDEN_CATEGORIES.find((c) => c.id === livabilityTrendCategory);
        const subColors = livabilityTrendCategory === 'built_env' ? colors.builtEnvBurdens : colors.burdens;
        // Traffic is from CalEnviroScreen (single snapshot) - use constant for flat line
        const trafficConst = displayPercentile(rows.find((r) => r.traffic_percentile != null && Number.isFinite(r.traffic_percentile))?.traffic_percentile ?? null);
        chartData = !cat ? { labels: years, datasets: [] } : {
          labels: years,
          datasets: cat.sub.map((s, i) => {
            const data = s.key === 'traffic_percentile'
              ? rows.map(() => trafficConst)
              : rows.map((row) => { const v = row[s.key as keyof LivabilityTrendRawRow]; return typeof v === 'number' && Number.isFinite(v) ? displayPercentile(v) : null; });
            return { label: s.label, data, borderColor: subColors[i % subColors.length], backgroundColor: 'transparent', borderWidth: 2, tension: 0.25 };
          })
        };
      }
    } else {
      if (livabilityTrendCategory === 'all') {
        chartData = { labels: years, datasets: BENEFIT_CATEGORIES.map((cat, i) => ({ label: cat.label, data: benefitCategorySeries.map((s) => s[cat.id] ?? null), borderColor: colors.benefits[i % colors.benefits.length], backgroundColor: 'transparent', borderWidth: 2, tension: 0.25 })) };
      } else {
        const cat = BENEFIT_CATEGORIES.find((c) => c.id === livabilityTrendCategory);
        chartData = !cat ? { labels: years, datasets: [] } : {
          labels: years,
          datasets: cat.sub.map((s, i) => ({ label: s.label, data: rows.map((row) => { const v = row[s.key as keyof LivabilityTrendRawRow]; return typeof v === 'number' && Number.isFinite(v) ? displayPercentile(v) : null; }), borderColor: colors.benefits[i % colors.benefits.length], backgroundColor: 'transparent', borderWidth: 2, tension: 0.25 }))
        };
      }
    }
    const sharedOptions = {
      type: 'line' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' as const, labels: { boxWidth: 12, padding: 12, usePointStyle: true } }, tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${typeof ctx.parsed.y === 'number' ? displayPercentile(ctx.parsed.y) : ctx.parsed.y}` } } },
      scales: { y: { min: undefined, max: undefined, title: { display: true, text: 'Percentile' } }, x: { min: 2015, max: 2023, title: { display: true, text: 'Year' } } }
    };
    return { totalsChartData: chartData, totalsChartOptions: { ...sharedOptions }, burdenCategories: BURDEN_CATEGORIES, benefitCategories: BENEFIT_CATEGORIES };
  }, [livabilityTrendRows, livabilityTrendView, livabilityTrendCategory]);

  const livabilityRankingForLocation = React.useMemo(() => {
    if (!selectedLocation || rankingTotalRows <= 0) return null;
    const rank = rankingByLocation.get(selectedLocation.toLowerCase());
    return typeof rank === 'number' && rank >= 1 ? { rank } : null;
  }, [selectedLocation, rankingTotalRows, rankingByLocation]);

  // Note: GHG Inventory loading is now handled by the Overview VMT chart's onDataCalculated callback
  // This ensures we wait for the VMT chart to finish loading before loading GHG Inventory

  // Track when Overview page is fully loaded. Only wait for VMT/GHG when those sections are shown.
  useEffect(() => {
    if (isLandingPage || !selectedLocation) {
      setOverviewFullyLoaded(false);
      if (typeof window !== 'undefined') {
        window.overviewFullyLoaded = false;
      }
      return;
    }

    const showTransportation = isOverviewSectionEnabled('transportation');
    const showGHG = isOverviewSectionEnabled('ghg_inventory');

    const vmtReady = !showTransportation || overviewVMTChartLoaded;
    const ghgReady = !showGHG || (ghgInventoryLoadedForLocationRef.current === selectedLocation && ghgInventoryData !== null);

    const isFullyLoaded = vmtReady && ghgReady;
    setOverviewFullyLoaded(isFullyLoaded);

    if (typeof window !== 'undefined') {
      window.overviewFullyLoaded = isFullyLoaded;
    }
  }, [overviewVMTChartLoaded, selectedLocation, isLandingPage, ghgInventoryData, isOverviewSectionEnabled, overviewSettingsByChartId]);

  // When VMT chart is disabled but GHG section is enabled, load GHG from preload path (VMT chart callback never runs)
  useEffect(() => {
    if (isLandingPage || !selectedLocation || !overviewSettingsLoaded) return;
    const showTransportation = isOverviewSectionEnabled('transportation');
    const showGHG = isOverviewSectionEnabled('ghg_inventory');
    if (!showGHG || showTransportation) return;
    if (!vmtDataAvailable || ghgInventoryData !== null) return;
    if (ghgInventoryLoadedForLocationRef.current === selectedLocation) return;
    if (ghgInventoryLoadingRef.current) return;

    ghgInventoryLoadingRef.current = true;
    fetchGHGInventoryData(selectedLocation)
      .then((ghgResult) => {
        setGhgInventoryData(ghgResult);
        ghgInventoryLoadedForLocationRef.current = selectedLocation;
      })
      .catch((err) => {
        setGhgInventoryData(null);
      })
      .finally(() => {
        ghgInventoryLoadingRef.current = false;
      });
  }, [overviewSettingsLoaded, selectedLocation, isLandingPage, vmtDataAvailable, ghgInventoryData, isOverviewSectionEnabled, overviewSettingsByChartId]);

  // Track when Overview load started (for "time left" indicator)
  useEffect(() => {
    if (selectedLocation && !isLandingPage && !overviewFullyLoaded) {
      if (overviewLoadStartRef.current === null) {
        overviewLoadStartRef.current = Date.now();
      }
    } else {
      overviewLoadStartRef.current = null;
      setOverviewLoadElapsedSec(0);
    }
  }, [selectedLocation, isLandingPage, overviewFullyLoaded]);

  // Update elapsed seconds every second while loading
  useEffect(() => {
    if (!selectedLocation || isLandingPage || overviewFullyLoaded) return;
    const interval = setInterval(() => {
      const start = overviewLoadStartRef.current;
      if (start == null) return;
      const elapsed = Math.floor((Date.now() - start) / 1000);
      setOverviewLoadElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedLocation, isLandingPage, overviewFullyLoaded]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        await supabaseLocationService.initialize();
        const locations = supabaseLocationService.getAvailableLocations();
        setAvailableLocations(locations);
      } catch (error) {
        console.error('Failed to load locations:', error);
        const locations = supabaseLocationService.getAvailableLocations();
        if (locations.length) setAvailableLocations(locations);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);


  // Reset dissolve state when location changes
  useEffect(() => {
    if (isLandingPage || !selectedLocation) return;
    setSectionOpacities({});
    OVERVIEW_SECTION_IDS.forEach((id) => { sectionRefsMap.current[id] = null; });
  }, [selectedLocation, isLandingPage]);

  // Make all sections visible on load (no scroll-to-reveal). Scroll main content into view so users see it without scrolling.
  useEffect(() => {
    if (isLandingPage || !selectedLocation || !overviewFullyLoaded) return;
    const all: Record<string, number> = {};
    OVERVIEW_SECTION_IDS.forEach((id) => { all[id] = 1; });
    setSectionOpacities(all);
    // Scroll overview content into view so Climate Equity Index and other sections are visible on load
    const content = scrollContainerRef.current;
    if (content) {
      content.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [overviewFullyLoaded, selectedLocation, isLandingPage]);

  const getSectionClassName = useCallback((_sectionId: string, baseClass: string) => {
    return `${baseClass} overview-dissolve-section`;
  }, []);

  // Preload data in background when location is selected on Overview dashboard
  useEffect(() => {
    if (isLandingPage || !selectedLocation || !selectedGeofips) {
        // Clear data when location is not available
        setGhgInventoryData(null);
        setVmtDataReady(false);
        setVmtDataAvailable(false);
        setOverviewVMTChartLoaded(false);
        setOverviewFullyLoaded(false);
        ghgInventoryLoadingRef.current = false; // Reset loading flag
        ghgInventoryLoadedForLocationRef.current = null; // Reset location tracking
        vmtChartDataLoadedRef.current = false; // Reset VMT chart loaded flag
        if (typeof window !== 'undefined') {
          window.overviewFullyLoaded = false;
        }
      return;
    }

    // Preload data in sequence: Load VMT first (from Transportation page if available, otherwise load it here), then GHG inventory, then other charts
    const preloadData = async () => {
      try {
        // Clear GHG Inventory data and VMT flags when starting preload for new location
        setGhgInventoryData(null);
        setVmtDataReady(false);
        setVmtDataAvailable(false);
        setOverviewVMTChartLoaded(false);
        setOverviewFullyLoaded(false);
        ghgInventoryLoadingRef.current = false; // Reset loading flag
        ghgInventoryLoadedForLocationRef.current = null; // Reset location tracking
        vmtChartDataLoadedRef.current = false; // Reset VMT chart loaded flag
        if (typeof window !== 'undefined') {
          window.overviewFullyLoaded = false;
        }
        
        // 1. Load VMT data FIRST (required for sector-based GHG inventory calculation)
        // Priority: Use VMT from Transportation page if available, otherwise load it here
        const { isVMTDataAvailable, setVMTDataFromTransportation } = await import('../../services/vmtDataService');
        
        // Check if VMT data is already available (from Transportation page)
        let vmtAvailable = isVMTDataAvailable(selectedLocation);
        setVmtDataAvailable(vmtAvailable);
        
        if (!vmtAvailable) {
          // Wait for VMT data from Transportation page (poll every 500ms, max 15 seconds)
          // This gives Transportation page time to load VMT in the background
          let attempts = 0;
          const maxAttempts = 30; // 15 seconds max wait
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              attempts++;
              vmtAvailable = isVMTDataAvailable(selectedLocation);
              setVmtDataAvailable(vmtAvailable);
              if (vmtAvailable) {
                clearInterval(checkInterval);
                setVmtDataReady(true);
                resolve();
              } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 500);
          });
        } else {
          setVmtDataReady(true);
        }

        // If VMT still not available, load it here (same method as Transportation page)
        if (!vmtAvailable) {
          try {
            // Fetch all three data sources like VMTChart does
            const [rawVmtDataResult, backcastedVmtDataResult, emfacDataResult] = await Promise.all([
              DataService.getVMTData(selectedLocation, undefined, selectedGeofips).catch(() => []),
              DataService.getVMTDataWithScaling(selectedGeofips, 'Combined', '2025', '2025').catch(() => []),
              DataService.getEMFACBackcastingDataByGeofips(selectedGeofips, [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], 'Combined').catch(() => [])
            ]);
            
            // Filter to only include PRIVATE_AUTO and ON_DEMAND modes (like VMTChart)
            const rawPrivateAuto = rawVmtDataResult.filter((d: any) => d.mode === 'PRIVATE_AUTO');
            const rawOnDemand = rawVmtDataResult.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
            const backcastedPrivateAuto = backcastedVmtDataResult.filter((d: any) => d.mode === 'PRIVATE_AUTO');
            const backcastedOnDemand = backcastedVmtDataResult.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
            
            // Get 2015 VMT data as the baseline for EMFAC backcasting
            const vmt2015PrivateAuto = backcastedPrivateAuto.find((d: any) => d.year === 2015);
            const vmt2015OnDemand = backcastedOnDemand.find((d: any) => d.year === 2015);
            const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);
            
            // Process EMFAC backcasting for 2005-2014 (same logic as VMTChart)
            const emfacBackcastedData: any[] = [];
            const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];
            
            if (vmt2015Total > 0 && emfacDataResult.length > 0) {
              // Sort ratios by year descending (2014, 2013, ..., 2005)
              const sortedRatios = emfacDataResult
                .filter((r: any) => emfacYears.includes(r.year))
                .sort((a: any, b: any) => b.year - a.year);
              
              // Calculate VMT for each year sequentially
              const vmtByYear = new Map<number, number>();
              vmtByYear.set(2015, vmt2015Total);
              
              // Apply ratios sequentially: year Y = year Y+1 * ratio_Y
              for (const emfacRecord of sortedRatios) {
                const nextYear = emfacRecord.year + 1;
                const nextYearVMT = vmtByYear.get(nextYear);
                
                if (nextYearVMT && emfacRecord.ratio) {
                  const currentYearVMT = nextYearVMT * emfacRecord.ratio;
                  vmtByYear.set(emfacRecord.year, currentYearVMT);
                }
              }
              
              // Create data points for each year, distributing between PRIVATE_AUTO and ON_DEMAND
              const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8;
              const onDemandRatio = 1 - privateAutoRatio;
              
              for (const year of emfacYears) {
                const yearVMT = vmtByYear.get(year);
                if (yearVMT !== undefined && yearVMT > 0) {
                  emfacBackcastedData.push({
                    year: year,
                    mode: 'PRIVATE_AUTO',
                    vmt: yearVMT * privateAutoRatio
                  });
                  emfacBackcastedData.push({
                    year: year,
                    mode: 'ON_DEMAND_AUTO',
                    vmt: yearVMT * onDemandRatio
                  });
                }
              }
            }
            
            // Combine all years: EMFAC (2005-2014) + AADT backcasted (2015-2022) + Raw (2023)
            const allYears = Array.from(new Set([
              ...emfacBackcastedData.map((d: any) => d.year),
              ...backcastedPrivateAuto.map((d: any) => d.year),
              ...backcastedOnDemand.map((d: any) => d.year),
              ...rawPrivateAuto.map((d: any) => d.year),
              ...rawOnDemand.map((d: any) => d.year)
            ])).sort();
            
            // Create combined VMT data for all years
            const combinedVmtData = allYears.map(year => {
              if (year === 2023) {
                // Use Replica data for 2023
                const privateAutoVmt = rawPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
                const onDemandVmt = rawOnDemand.find((d: any) => d.year === year)?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              } else if (year >= 2005 && year <= 2014) {
                // Use EMFAC backcasted data for 2005-2014
                const privateAutoVmt = emfacBackcastedData.find((d: any) => d.year === year && d.mode === 'PRIVATE_AUTO')?.vmt || 0;
                const onDemandVmt = emfacBackcastedData.find((d: any) => d.year === year && d.mode === 'ON_DEMAND_AUTO')?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              } else {
                // Use AADT backcasted data for 2015-2022
                const privateAutoVmt = backcastedPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
                const onDemandVmt = backcastedOnDemand.find((d: any) => d.year === year)?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              }
            });
            
            if (combinedVmtData.length > 0) {
              const vmtData = combinedVmtData.map((d: any) => ({
                year: d.year,
                vmt: d.vmt || 0,
                source: 'overview_preload',
                method_note: 'Preloaded from Overview page (Transportation page not visited)'
              }));
              
              // Set VMT data in the service FIRST
              setVMTDataFromTransportation(vmtData, selectedLocation);
              
              // Also set in window variable (legacy, but GHG Inventory checks this first)
              if (typeof window !== 'undefined') {
                window.vmtChartData = vmtData.map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0
                }));
              }
              
              // Verify VMT data is now available before proceeding
              const { isVMTDataAvailable: checkVMT } = await import('../../services/vmtDataService');
              const vmtNowAvailable = checkVMT(selectedLocation);
              
              if (vmtNowAvailable) {
              vmtAvailable = true;
                setVmtDataAvailable(true);
              setVmtDataReady(true);
              } else {
                console.error('❌ Overview: VMT data was set but isVMTDataAvailable() returned false');
                vmtAvailable = false;
                setVmtDataAvailable(false);
                setVmtDataReady(false);
              }
            } else if (rawVmtDataResult.length > 0) {
              // Fallback to raw data if backcasted data is not available
              const vmtData = rawVmtDataResult
                .filter((d: any) => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
                .reduce((acc: any, d: any) => {
                  const existing = acc.find((item: any) => item.year === d.year);
                  if (existing) {
                    existing.vmt += (d.vmt || 0);
                  } else {
                    acc.push({ year: d.year, vmt: d.vmt || 0 });
                  }
                  return acc;
                }, [])
                .map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0,
                  source: 'overview_preload_raw',
                  method_note: 'Preloaded from Overview page (raw data, Transportation page not visited)'
                }));
              
              setVMTDataFromTransportation(vmtData, selectedLocation);
              
              if (typeof window !== 'undefined') {
                window.vmtChartData = vmtData.map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0
                }));
              }
              
              // Verify VMT data is now available before proceeding
              const { isVMTDataAvailable: checkVMT } = await import('../../services/vmtDataService');
              const vmtNowAvailable = checkVMT(selectedLocation);
              
              if (vmtNowAvailable) {
              vmtAvailable = true;
                setVmtDataAvailable(true);
              setVmtDataReady(true);
              } else {
                console.error('❌ Overview: VMT data was set but isVMTDataAvailable() returned false');
                vmtAvailable = false;
                setVmtDataAvailable(false);
                setVmtDataReady(false);
              }
            }
          } catch {
            // VMT load failed silently
          }
        } else {
          // VMT was already available from Transportation page
          setVmtDataAvailable(true);
          setVmtDataReady(true);
        }

        // 2. Wait for Overview page VMT chart to load before loading GHG Inventory
        // The VMT chart will load data from Transportation page if available
        // We need to wait for the chart to finish loading before proceeding
        
        // Don't preload GHG Inventory here - wait for VMT chart onDataCalculated callback

        // 3-7. Load all independent data in parallel (don't depend on VMT or each other)
        await Promise.all([
          // 3. Consumption-based GHG data (independent of VMT)
          fetchConsumptionBasedGHGData(selectedLocation)
            .then((consumptionResult) => {
          setConsumptionGHGData(consumptionResult);
            })
            .catch(() => {}),

          // 4. Housing Burden percentile (independent of VMT)
          (async () => {
        try {
          // Get latest year's renter percentile (35%+ burden)
          const renterResult = await supabaseClient
            .from('housing_burden_renter')
            .select('year, percentile')
            .ilike('location_name', `%${selectedLocation}%`)
            .eq('housing_burden', '35.0 percent or more')
            .not('percentile', 'is', null)
            .gt('percentile', 0)
            .order('year', { ascending: false })
            .limit(1);
          
          if (renterResult.data && renterResult.data.length > 0 && renterResult.data[0].percentile) {
            const percentile100 = normalizePercentile(renterResult.data[0].percentile);
            setHousingBurdenPercentile(percentile100);
          } else {
            // Try owner data as fallback
            const ownerResult = await supabaseClient
              .from('owner_housing_burden')
              .select('year, percentile')
              .ilike('location_name', `%${selectedLocation}%`)
              .eq('owner_housing_burden_level', '35.0 percent or more')
              .eq('mortgage_status', 'All')
              .not('percentile', 'is', null)
              .gt('percentile', 0)
              .order('year', { ascending: false })
              .limit(1);
            
            if (ownerResult.data && ownerResult.data.length > 0 && ownerResult.data[0].percentile) {
              const percentile100 = normalizePercentile(ownerResult.data[0].percentile);
              setHousingBurdenPercentile(percentile100);
            }
          }
        } catch {
          // Housing burden preload failed silently
        }
          })(),

          // 5-6. Electricity and Natural Gas data (load once, use for both)
          (async () => {
            try {
              const [elecRes, elecCom, gasRes, gasCom] = await Promise.all([
                fetchElectricitySeries(selectedLocation, 'residential').catch(() => null),
                fetchElectricitySeries(selectedLocation, 'commercial').catch(() => null),
                fetchNaturalGasSeries(selectedLocation, 'residential').catch(() => null),
                fetchNaturalGasSeries(selectedLocation, 'commercial').catch(() => null)
              ]);
              
              // Process Electricity data
              if (elecRes && elecCom) {
                const elecStackedData = calculateStackedData(elecRes, elecCom, 'Electricity');
                if (elecStackedData) {
                  setElecStacked(elecStackedData);
                  setElecChangeSince2005(calcPercentChangeSince(elecStackedData, 2005));
                }
              }
              
              // Process Natural Gas data
          if (gasRes && gasCom) {
            const gasStackedData = calculateStackedData(gasRes, gasCom, 'Natural Gas');
            if (gasStackedData) {
              setGasStacked(gasStackedData);
              setGasChangeSince2005(calcPercentChangeSince(gasStackedData, 2005));
            }
              }
              
        } catch {
              // Electricity/Natural Gas preload failed silently
            }
          })(),

          // 7. Solar PV data - calculate cumulative capacity (independent of VMT)
          (async () => {
            try {
              if (selectedGeofips) {
                // Fetch solar data to calculate cumulative capacity
                const { data: solarData } = await supabaseClient
                  .from('all_pv_size')
                  .select('year, sector, total_watts')
                  .eq('geoid', selectedGeofips)
                  .lte('year', 2024)
                  .order('year', { ascending: true });
                
                if (solarData && solarData.length > 0) {
                  // Calculate cumulative capacity by summing all installations across all years
                  // This matches how CumulativeSolarCapacityChart calculates it
                  const yearSectorMap = new Map<number, Map<string, number>>();
                  solarData.forEach((record: any) => {
                    const year = parseInt(record.year.toString());
                    if (year > 2024) return;
                    
                    if (!yearSectorMap.has(year)) {
                      yearSectorMap.set(year, new Map());
                    }
                    const sectorMap = yearSectorMap.get(year)!;
                    const sector = record.sector || 'unknown';
                    const capacity = (parseFloat(record.total_watts.toString()) || 0) / 1000; // Convert to kW
                    sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacity);
                  });
                  
                  // Calculate cumulative capacity (sum all sectors across all years)
                  const allSectors = new Set<string>();
                  yearSectorMap.forEach(sectorMap => {
                    sectorMap.forEach((_, sector) => allSectors.add(sector));
                  });
                  
                  const sortedYears = Array.from(yearSectorMap.keys()).sort();
                  const sectorCumulativeMap = new Map<string, number>();
                  allSectors.forEach(sector => sectorCumulativeMap.set(sector, 0));
                  
                  sortedYears.forEach(year => {
                    const yearSectorMap_data = yearSectorMap.get(year) || new Map();
                    allSectors.forEach(sector => {
                      const sectorCapacity = yearSectorMap_data.get(sector) || 0;
                      const cumulativeSectorCapacity = sectorCumulativeMap.get(sector)! + sectorCapacity;
                      sectorCumulativeMap.set(sector, cumulativeSectorCapacity);
                    });
                  });
                  
                  const cumulativeCapacityKW = Array.from(sectorCumulativeMap.values()).reduce((sum, val) => sum + val, 0);
                  const cumulativeMW = cumulativeCapacityKW / 1000; // Convert kW to MW
                  setCumulativeSolarCapacity(cumulativeMW);
                }
              }
        } catch {
          // Solar PV preload failed silently
        }
          })()
        ]);

      } catch (error) {
        console.error('Overview: Error during background preload:', error);
      }
    };

    // Start preloading in background (don't await)
    preloadData();
  }, [selectedLocation, selectedGeofips, isLandingPage]);

  // Do not call getAvailableLocations() before initialize() finishes — it logs a warning every render.
  const locationsForStats = React.useMemo(() => {
    if (!supabaseLocationService.isUsingSupabase()) {
      return availableLocations;
    }
    const fromService = supabaseLocationService.getAvailableLocations();
    return fromService.length > 0 ? fromService : availableLocations;
  }, [availableLocations]);
  const cities = locationsForStats.filter((loc: LocationInfo) => loc.type === 'city');
  const counties = locationsForStats.filter((loc: LocationInfo) => loc.type === 'county');

  const equityCompareLocationOptions = React.useMemo(
    () =>
      [...cities, ...counties].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [cities, counties]
  );

  const geofipsForEquityCompareLocation = React.useCallback((locationName: string): number | null => {
    if (!locationName) return null;
    return resolveByCapName(locationName.trim())?.populationGeofips ?? null;
  }, []);

  useEffect(() => {
    if (equitySideBySide && selectedLocation) {
      setEquityCompareLeft(selectedLocation);
    }
  }, [equitySideBySide, selectedLocation]);

  // If this is the landing page, show redesigned homepage
  if (isLandingPage) {
    return (
      <HomePage
        onLocationSelect={onLocationSelect}
        citiesCount={cities.length}
        countiesCount={counties.length}
        communitiesCount={locationsForStats.length}
        loading={loading}
      />
    );
  }

  // Dashboard view - show when isLandingPage is false
  // If no location selected, show message to select location
  if (!selectedLocation) {
    return (
      <div className="overview-page">
        <PageHeader 
          title="Overview"
          subtitle="Please select a location to view the overview dashboard."
          variant="overview"
        />
        <div className="overview-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '30px' }}>
            Select a location from the header to view climate action data and metrics.
          </p>
        </div>
      </div>
    );
  }

  // Dashboard with location selected
  const estimatedSecLeft = Math.max(0, TYPICAL_OVERVIEW_LOAD_SEC - overviewLoadElapsedSec);

  // Full-screen loading overlay: mask until settings loaded and chart data ready (prevents mounting disabled sections)
  const showOverviewLoadingScreen = Boolean(selectedLocation && (!overviewSettingsLoaded || !overviewFullyLoaded));

  return (
    <div className="overview-page" data-outreach-snapshot="overview">
      <PageHeader 
        title={`Overview - ${selectedLocation}`}
        subtitle="Sample of Climate Action data and figures"
        variant="overview"
      />

      {hasAnyRole(['super_admin', 'local_govt_admin']) && selectedGeofips != null ? (
        <div className="overview-outreach-cta">
          <button
            type="button"
            className="overview-outreach-cta__btn"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('geofips', String(selectedGeofips));
              params.set('jurisdiction', selectedLocation);
              navigate(`/admin/outreach?${params.toString()}`);
            }}
          >
            Create outreach message
          </button>
        </div>
      ) : null}

      {showOverviewLoadingScreen && (
        <div
          className="overview-loading-screen"
          role="status"
          aria-live="polite"
          aria-label="Loading overview"
        >
          <div className="overview-loading-screen__spinner" />
          <p className="overview-loading-screen__title">Loading overview…</p>
          <p className="overview-loading-screen__subtitle">Preparing figures for {selectedLocation}</p>
          <p className="overview-loading-screen__hint">
            {overviewLoadElapsedSec >= TYPICAL_OVERVIEW_LOAD_SEC
              ? 'Almost ready…'
              : `About ${estimatedSecLeft}–${Math.max(1, estimatedSecLeft + 8)} sec left`}
          </p>
        </div>
      )}

      <div className="overview-content overview-dissolve-container" ref={scrollContainerRef}>
        {/* Section 1: Burden/Benefits Charts */}
        {isOverviewSectionEnabled('equity_index') && (
          <div
            className={getSectionClassName('equity-index', 'tool-section tool-section-gray')}
            data-section-id="equity-index"
            ref={(el) => { sectionRefsMap.current['equity-index'] = el; }}
            style={{ opacity: sectionOpacities['equity-index'] ?? 0, transform: `scale(${0.96 + 0.04 * (sectionOpacities['equity-index'] ?? 0)})` }}
          >
            {SECTION_BG_IMAGES['equity-index'] && <img src={SECTION_BG_IMAGES['equity-index']} alt="" aria-hidden="true" className="overview-section-bg-img" loading="lazy" decoding="async" style={{ opacity: 0.25 * (sectionOpacities['equity-index'] ?? 0) }} />}
            <div className="section-container">
              <h2 className="overview-section-title">
                {(() => {
                  const selectedRecord = availableLocations.find((loc) => loc.name === selectedLocation);
                  const isCity = selectedRecord?.type !== 'county';
                  const geoMatch = rankingGeographyType === (isCity ? 'City' : 'County');
                  const rank =
                    geoMatch && overallPeerRankMeta && selectedLocation
                      ? overallPeerRankMeta.rankByLocation.get(selectedLocation.toLowerCase())
                      : undefined;
                  const n = overallPeerRankMeta?.n ?? 0;
                  const peerPct =
                    typeof rank === 'number' && rank >= 1 && n > 1 ? peerPercentileAmongRank(rank, n) : null;
                  const amongLabel = isCity ? 'cities' : 'counties';
                  const mid = Math.floor((n + 1) / 2);
                  const useHighest = typeof rank === 'number' && rank >= 1 && rank <= mid;
                  const rankOrdinal =
                    typeof rank === 'number' && rank >= 1 && n >= 1
                      ? useHighest
                        ? ordinalRank(rank)
                        : ordinalRank(n - rank + 1)
                      : null;
                  const superlative = useHighest ? 'highest' : 'lowest';
                  const roundedPct = peerPct != null ? Math.round(peerPct) : null;
                  return renderSampleTitle(
                    peerPct != null && roundedPct != null && selectedLocation && rankOrdinal ? (
                      <>
                        Climate Equity Index
                        <br />
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {selectedLocation} ranks {rankOrdinal} {superlative} of {n} {amongLabel}{' '}
                          (
                          <span style={{ color: equityPeerPercentileColor(roundedPct), fontWeight: 700 }}>
                            {ordinalRank(roundedPct)} Percentile
                          </span>
                          )
                        </span>
                      </>
                    ) : (
                      'Climate Equity Index'
                    ),
                    false
                  );
                })()}
              </h2>

              <div className="overview-equity-compare-toolbar">
                <label className="overview-equity-compare-toggle">
                  <input
                    type="checkbox"
                    checked={equitySideBySide}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setEquitySideBySide(enabled);
                      if (enabled) {
                        setEquityCompareLeft(selectedLocation);
                        if (
                          !isOverviewChartEnabled('equity_index', 'burdens') &&
                          isOverviewChartEnabled('equity_index', 'benefits')
                        ) {
                          setEquityCompareView('benefits');
                        } else if (
                          isOverviewChartEnabled('equity_index', 'burdens') &&
                          !isOverviewChartEnabled('equity_index', 'benefits')
                        ) {
                          setEquityCompareView('burdens');
                        }
                      }
                    }}
                  />
                  Side-by-side comparison
                </label>
                {equitySideBySide && (
                  <>
                    <label className="overview-equity-compare-field">
                      <span className="overview-equity-compare-field__label">Show</span>
                      <select
                        value={equityCompareView}
                        onChange={(e) => setEquityCompareView(e.target.value as 'burdens' | 'benefits')}
                        className="overview-equity-compare-select"
                      >
                        {isOverviewChartEnabled('equity_index', 'burdens') && (
                          <option value="burdens">Burdens</option>
                        )}
                        {isOverviewChartEnabled('equity_index', 'benefits') && (
                          <option value="benefits">Benefits</option>
                        )}
                      </select>
                    </label>
                    <div className="overview-equity-compare-field overview-equity-compare-field--location">
                      <span className="overview-equity-compare-field__label">Left</span>
                      <HeaderLocationSelector
                        className="overview-equity-compare-location"
                        inputId="equity-compare-location-left"
                        inputName="climateplans-equity-compare-left"
                        currentLocation={equityCompareLeft}
                        placeholder="Search city or county…"
                        onLocationSelect={setEquityCompareLeft}
                      />
                    </div>
                    <div className="overview-equity-compare-field overview-equity-compare-field--location">
                      <span className="overview-equity-compare-field__label">Right</span>
                      <HeaderLocationSelector
                        className="overview-equity-compare-location"
                        inputId="equity-compare-location-right"
                        inputName="climateplans-equity-compare-right"
                        currentLocation={equityCompareRight}
                        placeholder="Search city or county…"
                        onLocationSelect={setEquityCompareRight}
                      />
                    </div>
                  </>
                )}
              </div>

              {equitySideBySide ? (
                <div className="overview-equity-compare-grid">
                  {(['left', 'right'] as const).map((side) => {
                    const locationName = side === 'left' ? equityCompareLeft : equityCompareRight;
                    const compareType = equityCompareView;
                    const typeEnabled =
                      compareType === 'burdens'
                        ? isOverviewChartEnabled('equity_index', 'burdens')
                        : isOverviewChartEnabled('equity_index', 'benefits');
                    const cardClass =
                      compareType === 'burdens'
                        ? 'overview-equity-card overview-equity-card--burdens'
                        : 'overview-equity-card overview-equity-card--benefits';
                    const titleColor = compareType === 'burdens' ? '#dc2626' : '#10b981';
                    return (
                      <div key={side} className={cardClass}>
                        <h3
                          style={{
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            marginBottom: '4px',
                            textAlign: 'center',
                            color: titleColor,
                          }}
                        >
                          {compareType === 'burdens' ? 'Burdens' : 'Benefits'}
                        </h3>
                        <div
                          className="overview-text-secondary"
                          style={{ fontSize: '0.95rem', textAlign: 'center', marginBottom: '16px' }}
                        >
                          {side === 'left' ? 'Left panel' : 'Right panel'} (percentiles)
                        </div>
                        {!typeEnabled ? (
                          <p className="overview-text-secondary" style={{ textAlign: 'center' }}>
                            This view is disabled in overview settings.
                          </p>
                        ) : !locationName ? (
                          <p className="overview-text-secondary" style={{ textAlign: 'center', padding: '24px 12px' }}>
                            Choose a location for the {side} panel.
                          </p>
                        ) : (
                          <BurdenBenefitsChart
                            key={`${side}-${locationName}-${compareType}`}
                            type={compareType}
                            locationOverride={{
                              name: locationName,
                              geofips: geofipsForEquityCompareLocation(locationName),
                            }}
                            panelLabel={locationName}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '20px',
                    marginBottom: '20px',
                  }}
                >
                  {isOverviewChartEnabled('equity_index', 'burdens') && (
                    <div className="overview-equity-card overview-equity-card--burdens">
                      <h3
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textAlign: 'center',
                          color: '#dc2626',
                        }}
                      >
                        Burdens
                      </h3>
                      <div
                        className="overview-text-secondary"
                        style={{ fontSize: '1rem', textAlign: 'center', marginBottom: '20px' }}
                      >
                        (percentiles)
                      </div>
                      <BurdenBenefitsChart
                        type="burdens"
                        housingBurdenPercentile={housingBurdenPercentile}
                        housingBurdenRenterPercentile={housingBurdenRenterPercentile}
                        housingBurdenOwnerPercentile={housingBurdenOwnerPercentile}
                        fossilHeatingLivabilityPercentile={fossilHeatingLivabilityPercentile}
                        transportationCostBurdenLivabilityPercentile={transportationCostBurdenLivabilityPercentile}
                      />
                    </div>
                  )}
                  {isOverviewChartEnabled('equity_index', 'benefits') && (
                    <div className="overview-equity-card overview-equity-card--benefits">
                      <h3
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textAlign: 'center',
                          color: '#10b981',
                        }}
                      >
                        Benefits
                      </h3>
                      <div
                        className="overview-text-secondary"
                        style={{ fontSize: '1rem', textAlign: 'center', marginBottom: '20px' }}
                      >
                        (percentiles)
                      </div>
                      <BurdenBenefitsChart
                        type="benefits"
                        fossilHeatingLivabilityPercentile={fossilHeatingLivabilityPercentile}
                        employmentRateLivabilityPercentile={employmentRateLivabilityPercentile}
                        publicTransitLivabilityPercentile={publicTransitLivabilityPercentile}
                      />
                    </div>
                  )}
                </div>
              )}

              {showLivabilityOverTimeChart && (
                <div className="overview-equity-card" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginTop: '20px', background: 'var(--card-bg)' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {selectedLocation} Climate Equity Over Time
                    {livabilityRankingChangeFrom2005 && (
                      <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
                        {' '}
                        ({livabilityRankingChangeFrom2005.pointsDelta >= 0 ? '+' : ''}
                        {livabilityRankingChangeFrom2005.pointsDelta} pts, {livabilityRankingChangeFrom2005.rankDelta >= 0 ? '+' : ''}
                        {livabilityRankingChangeFrom2005.rankDelta} ranking from {livabilityRankingChangeFrom2005.startYear} to {livabilityRankingChangeFrom2005.endYear})
                      </span>
                    )}
                  </h3>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    {livabilityTrendView === 'overall'
                      ? 'Tracks the overall climate equity index score (percentile) over time.'
                      : livabilityTrendView === 'burdens'
                        ? 'Burden categories and subcategories over time (percentile).'
                        : 'Benefit categories and subcategories over time (percentile).'}
                  </div>
                  {!livabilityTrendLoading && !livabilityTrendError && livabilityTrendFigure && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                      <label style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                        View:
                        <select
                          value={livabilityTrendView}
                          onChange={(e) => {
                            setLivabilityTrendView(e.target.value as 'overall' | 'burdens' | 'benefits');
                            setLivabilityTrendCategory('all');
                          }}
                          style={{ marginLeft: '8px', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '0.9rem', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                        >
                          <option value="overall">Overall (total score)</option>
                          <option value="burdens">Burdens (5 major categories)</option>
                          <option value="benefits">Benefits (5 major categories)</option>
                        </select>
                      </label>
                      {(livabilityTrendView === 'burdens' || livabilityTrendView === 'benefits') && (
                        <label style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                          Category:
                          <select
                            value={livabilityTrendCategory}
                            onChange={(e) => setLivabilityTrendCategory(e.target.value)}
                            style={{ marginLeft: '8px', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '0.9rem', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                          >
                            <option value="all">All 5 categories</option>
                            {(livabilityTrendView === 'burdens' ? livabilityTrendFigure.burdenCategories : livabilityTrendFigure.benefitCategories).map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>
                  )}
                  {livabilityTrendLoading && <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Loading climate equity trend...</div>}
                  {livabilityTrendError && !livabilityTrendLoading && <div style={{ color: '#b91c1c', fontSize: '0.95rem' }}>{livabilityTrendError}</div>}
                  {!livabilityTrendLoading && !livabilityTrendError && livabilityTrendFigure && (
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>
                        {livabilityTrendView === 'overall' ? 'Total score' : livabilityTrendCategory === 'all'
                          ? (livabilityTrendView === 'burdens' ? 'All 5 Burden Categories' : 'All 5 Benefit Categories')
                          : ((livabilityTrendView === 'burdens' ? livabilityTrendFigure.burdenCategories.find((c) => c.id === livabilityTrendCategory)?.label : livabilityTrendFigure.benefitCategories.find((c) => c.id === livabilityTrendCategory)?.label) ?? 'Subcategories') + ' (subcategories)'}
                      </h4>
                      <div style={{ height: '280px' }}>
                        <ChartRendererSupabase chartData={livabilityTrendFigure.totalsChartData} chartOptions={livabilityTrendFigure.totalsChartOptions} height="280px" location={selectedLocation} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showLivabilityRankingsChart && (
                <div className="overview-equity-card" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', marginTop: '16px', background: 'var(--card-bg)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      Climate Equity Rankings
                      {rankingView !== 'overall' && (() => {
                        const cat = rankingCategory !== 'all' ? (rankingView === 'burdens' ? LIVABILITY_BURDEN_CATEGORIES : LIVABILITY_BENEFIT_CATEGORIES).find((c) => c.id === rankingCategory) : undefined;
                        const sub = rankingSubcategory && cat ? cat.sub.find((s: { key: string; label: string }) => s.key === rankingSubcategory) : undefined;
                        return (
                          <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
                            {' '}— {rankingView === 'burdens' ? 'Burdens' : 'Benefits'}
                            {cat && ` › ${cat.label}`}
                            {sub && ` › ${sub.label}`}
                          </span>
                        );
                      })()}
                      {' '}({rankingYear})
                      {livabilityRankingForLocation && (() => {
                        const geoLabel = rankingGeographyType === 'City' ? 'cities' : 'counties';
                        return (
                          <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}> — {selectedLocation} ranks {ordinalRank(livabilityRankingForLocation.rank)} of {rankingTotalRows} {geoLabel}</span>
                        );
                      })()}
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        View
                        <select value={rankingView} onChange={(e) => { setRankingView(e.target.value as 'overall' | 'burdens' | 'benefits'); setRankingCategory('all'); setRankingSubcategory(''); }} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                          <option value="overall">Overall (total score)</option>
                          <option value="burdens">Burdens (5 categories)</option>
                          <option value="benefits">Benefits (5 categories)</option>
                        </select>
                      </label>
                      {(rankingView === 'burdens' || rankingView === 'benefits') && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                          Category
                          <select value={rankingCategory} onChange={(e) => { setRankingCategory(e.target.value); setRankingSubcategory(''); }} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                            <option value="all">All 5 categories</option>
                            {(rankingView === 'burdens' ? LIVABILITY_BURDEN_CATEGORIES : LIVABILITY_BENEFIT_CATEGORIES).map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                      {(rankingView === 'burdens' || rankingView === 'benefits') && rankingCategory !== 'all' && (() => {
                        const cat = (rankingView === 'burdens' ? LIVABILITY_BURDEN_CATEGORIES : LIVABILITY_BENEFIT_CATEGORIES).find((c) => c.id === rankingCategory);
                        return cat ? (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                            Subcategory
                            <select value={rankingSubcategory} onChange={(e) => setRankingSubcategory(e.target.value)} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                              <option value="">Category average</option>
                              {cat.sub.map((s) => (
                                <option key={s.key} value={s.key}>{s.label}</option>
                              ))}
                            </select>
                          </label>
                        ) : null;
                      })()}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        Geography
                        <select value={rankingGeographyType} onChange={(e) => setRankingGeographyType(e.target.value as 'City' | 'County')} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                          <option value="City">Cities</option>
                          <option value="County">Counties</option>
                        </select>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        Year
                        <select value={rankingYear} onChange={(e) => setRankingYear(Number(e.target.value))} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                          {rankingAvailableYears.map((year) => <option key={year} value={year}>{year}</option>)}
                        </select>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        Rows
                        <select value={rankingRowCount} onChange={(e) => setRankingRowCount(e.target.value === 'all' ? 'all' : Number(e.target.value) as RankingRowCount)} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '4px 8px', background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value="all">All</option>
                        </select>
                      </label>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    {rankingView === 'overall' &&
                      'Overall climate equity uses the precalculated total score percentile (higher = better vs peers); when missing, (Benefits + (100 − Burdens)) / 2. '}
                    {rankingView === 'burdens' && 'Burden rank: lower burden percentile = better. Score = 100 − burden percentile. '}
                    {rankingView === 'benefits' && 'Benefit rank: higher benefit percentile = better. Score = percentile. '}
                    {rankingTotalRows > 0 && (
                      <span>
                        {rankingRowCount === 'all'
                          ? `Showing all ${rankingTotalRows} ${rankingGeographyType === 'City' ? 'cities' : 'counties'}.`
                          : rankingDisplayCenteredOnSelection && selectedLocation?.trim()
                            ? `Showing ${rankingDisplayRows.length} of ${rankingTotalRows} ${rankingGeographyType === 'City' ? 'cities' : 'counties'}, centered on ${selectedLocation}.`
                            : `Showing ${rankingDisplayRows.length} of ${rankingTotalRows} ${rankingGeographyType === 'City' ? 'cities' : 'counties'} (starts at rank 1).`}
                      </span>
                    )}
                  </div>
                  {rankingLoading && <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Loading rankings...</div>}
                  {rankingError && !rankingLoading && <div style={{ color: '#b91c1c', fontSize: '0.95rem' }}>{rankingError}</div>}
                  {!rankingLoading && !rankingError && (
                    <div style={{ width: '100%' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#166534' }}>Highest</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: 'var(--section-bg-alt)' }}>
                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid var(--border-color)' }}>#</th>
                            <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid var(--border-color)' }}>Location</th>
                            <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid var(--border-color)' }}>Score</th>
                            <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid var(--border-color)' }}>Percentile</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rankingDisplayRows.map((row) => (
                            <tr key={`rank-${row.geofips}`} style={{ backgroundColor: selectedLocation && row.locationName.toLowerCase() === selectedLocation.toLowerCase() ? 'var(--header-button-hover-bg)' : 'transparent' }}>
                              <td style={{ padding: '6px', borderBottom: '1px solid var(--border-color)' }}>{row.rank}</td>
                              <td style={{ padding: '6px', borderBottom: '1px solid var(--border-color)' }}>{row.locationName}</td>
                              <td style={{ padding: '6px', borderBottom: '1px solid var(--border-color)', textAlign: 'right', fontWeight: 600 }}>{(displayPercentile(row.score) ?? row.score).toFixed(0)}</td>
                              <td style={{ padding: '6px', borderBottom: '1px solid var(--border-color)', textAlign: 'right', fontWeight: 600 }}>
                                {(() => {
                                  const p = peerPercentileFromOverallMeta(overallPeerRankMeta, row.locationName);
                                  if (p == null) return <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>—</span>;
                                  const rounded = Math.round(p);
                                  return (
                                    <span style={{ color: equityPeerPercentileColor(rounded) }}>
                                      {ordinalRank(rounded)} Percentile
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 2: Buildings */}
        {isOverviewSectionEnabled('buildings') && (
          <div
            className={getSectionClassName('buildings', 'tool-section tool-section-white')}
            data-section-id="buildings"
            ref={(el) => { sectionRefsMap.current['buildings'] = el; lazyBuildings.sentinelRef.current = el; }}
            style={{ opacity: sectionOpacities['buildings'] ?? 0, transform: `scale(${0.96 + 0.04 * (sectionOpacities['buildings'] ?? 0)})` }}
          >
            {SECTION_BG_IMAGES['buildings'] && <img src={SECTION_BG_IMAGES['buildings']} alt="" aria-hidden="true" className="overview-section-bg-img" loading="lazy" decoding="async" style={{ opacity: 0.25 * (sectionOpacities['buildings'] ?? 0) }} />}
            <div className="section-container">
              <h2 className="overview-section-title">
                {renderSampleTitle('Buildings & Energy')}
              </h2>
              {lazyBuildings.shouldMount ? <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('buildings', 'electricity_consumption') && (
                <div className="overview-chart-card" onClick={() => handleNavigateWithCheck('/buildings')}>
                  <h4 className="overview-chart-title-lg">
                    Electricity Consumption{elecChangeSince2005 !== null ? `: ${elecChangeSince2005 > 0 ? '+' : ''}${elecChangeSince2005.toFixed(1)}% ${elecChangeSince2005 > 0 ? 'Increase' : 'Decrease'}` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    {elecStacked ? (
                      <ChartRendererSupabase 
                        chartData={elecStacked.chartData} 
                        chartOptions={elecStacked.chartOptions} 
                        height="250px" 
                      location={selectedLocation} 
                      />
                    ) : (
                      <div className="overview-text-secondary" style={{ textAlign: 'center', padding: '20px' }}>
                        <p>Loading Electricity data...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('buildings', 'natural_gas_consumption') && (
                <div className="overview-chart-card" onClick={() => handleNavigateWithCheck('/buildings')}>
                  <h4 className="overview-chart-title-lg">
                    Natural Gas Consumption{gasChangeSince2005 !== null ? `: ${gasChangeSince2005 > 0 ? '+' : ''}${gasChangeSince2005.toFixed(1)}% ${gasChangeSince2005 > 0 ? 'Increase' : 'Decrease'}` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    {gasStacked ? (
                      <ChartRendererSupabase 
                        chartData={gasStacked.chartData} 
                        chartOptions={gasStacked.chartOptions} 
                        height="250px" 
                      location={selectedLocation} 
                    />
                    ) : (
                      <div className="overview-text-secondary" style={{ textAlign: 'center', padding: '20px' }}>
                        <p>Loading Natural Gas data...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('buildings', 'cumulative_solar_capacity') && (
                <div className="overview-chart-card" onClick={() => handleNavigateWithCheck('/buildings')}>
                  <h4 className="overview-chart-title-lg">
                    Cumulative Solar Capacity{cumulativeSolarCapacity !== null ? `: ${cumulativeSolarCapacity.toFixed(1)} MW` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    {selectedGeofips ? (
                      <div style={{ height: '250px', width: '100%' }}>
                        <CumulativeSolarCapacityChart 
                      location={selectedLocation} 
                          geofips={selectedGeofips}
                      embedOnly={true}
                    />
                  </div>
                    ) : (
                      <div className="overview-text-secondary" style={{ textAlign: 'center', padding: '20px' }}>
                        <p>Loading Solar PV data...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div> : <div style={{ minHeight: '300px' }} />}
              <MoreFiguresButton path="/buildings" label="More Buildings & Energy Figures" onClick={handleNavigateWithCheck} />
            </div>
          </div>
        )}

        {/* Section 3: Transportation - defer chart mount until settled to prevent flicker */}
        {isOverviewSectionEnabled('transportation') && (
          <div
            className={getSectionClassName('transportation', 'tool-section tool-section-gray')}
            data-section-id="transportation"
            ref={(el) => { sectionRefsMap.current['transportation'] = el; lazyTransportation.sentinelRef.current = el; }}
            style={{ opacity: sectionOpacities['transportation'] ?? 0, transform: `scale(${0.96 + 0.04 * (sectionOpacities['transportation'] ?? 0)})` }}
          >
            {SECTION_BG_IMAGES['transportation'] && <img src={SECTION_BG_IMAGES['transportation']} alt="" aria-hidden="true" className="overview-section-bg-img" loading="lazy" decoding="async" style={{ opacity: 0.25 * (sectionOpacities['transportation'] ?? 0) }} />}
            <div className="section-container">
              <h2 className="overview-section-title">
                {renderSampleTitle('Transportation')}
              </h2>
              {lazyTransportation.shouldMount ? <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '15px',
                marginBottom: '20px'
              }}>
              {!transportationSectionSettled ? (
                /* Placeholder preserves layout; charts mount only after settle to avoid flicker */
                <>
                  <div className="overview-chart-loading">Loading…</div>
                  <div className="overview-chart-loading">Loading…</div>
                  <div className="overview-chart-loading">Loading…</div>
                </>
              ) : (
                <>
                  {isOverviewChartEnabled('transportation', 'vehicle_miles') && (
                <div className="overview-chart-card overview-chart-card--tall" onClick={() => handleNavigateWithCheck('/transportation')}>
                  <h4 className="overview-chart-title">
                    Vehicle Miles{transportationSectionSettled && vmtChangeSince2005 !== null ? `: ${vmtChangeSince2005 > 0 ? '+' : ''}${vmtChangeSince2005.toFixed(1)}% ${vmtChangeSince2005 > 0 ? 'Increase' : 'Decrease'} since 2005` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', width: '100%', height: '100%', position: 'relative' }}>
                    <VMTChart 
                        location={selectedLocation} 
                      geofips={selectedGeofips || null}
                      chartView="total"
                      backcastingMethod="Combined"
                      emfac2005_2014Method="Combined"
                      emfacFleetVersion="2025"
                      embedOnly={true}
                      onMetricsCalculated={(metrics) =>
                        setVmtChangeDebounced(metrics.changeSince2005 ?? null)
                      }
                      onDataCalculated={(data) => {
                    if (!data || data.length === 0) {
                      return;
                    }
                    
                    if (vmtChartDataLoadedRef.current && ghgInventoryLoadedForLocationRef.current === selectedLocation) {
                      return;
                    }
                    
                    if (ghgInventoryLoadedForLocationRef.current && ghgInventoryLoadedForLocationRef.current !== selectedLocation) {
                      vmtChartDataLoadedRef.current = false;
                      ghgInventoryLoadedForLocationRef.current = null;
                      ghgInventoryLoadingRef.current = false;
                    }
                    
                    // Store VMT data in global service (same as Transportation page does)
                    // This ensures GHG Inventory can access the backcasted VMT data including years 2005-2014
                    setVMTDataFromTransportation(
                      data.map(d => ({
                        year: d.year,
                        vmt: d.vmt || 0,
                        source: 'overview_vmt_chart',
                        method_note: 'VMT data from Overview page VMT chart - includes backcasted years 2005-2014'
                      })),
                      selectedLocation
                    );
                    
                    // Also set legacy window variable for backward compatibility
                    if (typeof window !== 'undefined') {
                      window.vmtChartData = data.map((d: any) => ({
                        year: d.year,
                        vmt: d.vmt || 0
                      }));
                    }
                    
                    // Only update state flags once per location
                    if (!vmtChartDataLoadedRef.current) {
                      setOverviewVMTChartLoaded(true);
                      setVmtDataAvailable(true);
                      setVmtDataReady(true);
                      vmtChartDataLoadedRef.current = true;
                    }
                    
                    // Now that VMT chart has loaded, load GHG Inventory (only once per location)
                    if (selectedLocation && 
                        !ghgInventoryLoadingRef.current && 
                        ghgInventoryLoadedForLocationRef.current !== selectedLocation) {
                      ghgInventoryLoadingRef.current = true;
                      fetchGHGInventoryData(selectedLocation)
                        .then((ghgResult) => {
                          setGhgInventoryData(ghgResult);
                          ghgInventoryLoadedForLocationRef.current = selectedLocation;
                          ghgInventoryLoadingRef.current = false;
                        })
                        .catch(() => {
                          setGhgInventoryData(null);
                          ghgInventoryLoadingRef.current = false;
                        });
                    }
                  }}
                />
              </div>
            </div>
                  )}
                  {isOverviewChartEnabled('transportation', 'mode_share') && (
                <div className="overview-chart-card overview-chart-card--tall" onClick={() => handleNavigateWithCheck('/transportation')}>
                  <h4 className="overview-chart-title">
                    Mode Share{transportationSectionSettled && privateVehiclePercent !== null ? `: ${privateVehiclePercent.toFixed(1)}% Private Vehicle` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    <TransportationModeShare 
                        location={selectedLocation} 
                      geofips={selectedGeofips || null} 
                      embedOnly={true}
                      onModeShareCalculated={(data) => setPrivateVehicleDebounced(data.privateAuto)}
                      />
                  </div>
                </div>
                  )}
                  {isOverviewChartEnabled('transportation', 'ev_fleet_adoption') && (
                <div className="overview-chart-card overview-chart-card--tall" onClick={() => handleNavigateWithCheck('/transportation')}>
                  <h4 className="overview-chart-title">
                    EV Fleet Adoption{transportationSectionSettled && evPercent !== null ? `: ${evPercent.toFixed(1)}% of light-duty vehicles` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    <EVFleetAdoption 
                        location={selectedLocation} 
                      geofips={selectedGeofips || null} 
                      embedOnly={true}
                      onEvPercentCalculated={(percent) => setEvPercentDebounced(percent)}
                      />
                  </div>
                </div>
                  )}
                  {(
                    <div
                      className="overview-chart-card overview-chart-card--tall"
                      onClick={() => handleNavigateWithCheck('/transportation')}
                      style={{ gridColumn: 'span 1' }}
                    >
                      <h4 className="overview-chart-title">
                        Transportation Peer Comparison
                      </h4>
                      <div style={{ pointerEvents: 'none', flex: 1 }}>
                        <TransportationPeerComparisonCard
                          locationName={selectedLocation}
                          geofips={selectedGeofips || null}
                          geographyType={(() => {
                            const rec = availableLocations.find((loc) => loc.name === selectedLocation);
                            return rec?.type === 'county' ? 'County' : 'City';
                          })()}
                          year={2023}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              </div> : <div style={{ minHeight: '300px' }} />}
              <MoreFiguresButton path="/transportation" label="More Transportation Figures" onClick={handleNavigateWithCheck} />
            </div>
          </div>
        )}

        {/* Section 4: Waste+ - defer chart mount until settled to prevent flicker */}
        {isOverviewSectionEnabled('waste') && (
          <div
            className={getSectionClassName('waste', 'tool-section tool-section-white')}
            data-section-id="waste"
            ref={(el) => { sectionRefsMap.current['waste'] = el; lazyWaste.sentinelRef.current = el; }}
            style={{ opacity: sectionOpacities['waste'] ?? 0, transform: `scale(${0.96 + 0.04 * (sectionOpacities['waste'] ?? 0)})` }}
          >
            {SECTION_BG_IMAGES['waste'] && <img src={SECTION_BG_IMAGES['waste']} alt="" aria-hidden="true" className="overview-section-bg-img" loading="lazy" decoding="async" style={{ opacity: 0.25 * (sectionOpacities['waste'] ?? 0) }} />}
            <div className="section-container">
              <h2 className="overview-section-title">
                {renderSampleTitle('Waste +')}
              </h2>
              {lazyWaste.shouldMount ? <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {!wasteSectionSettled ? (
                <>
                  <div className="overview-chart-loading overview-chart-loading--tall">Loading…</div>
                  <div className="overview-chart-loading overview-chart-loading--tall">Loading…</div>
                </>
              ) : (
                <>
              {isOverviewChartEnabled('waste', 'waste_tonnage') && (
                <div className="overview-chart-card" style={{ minHeight: '500px', height: '500px' }} onClick={() => handleNavigateWithCheck('/other')}>
                  <h4 className="overview-chart-title-lg">
                    Waste Tonnage{wasteSectionSettled && wasteChangeSince2005 !== null ? `: ${wasteChangeSince2005 > 0 ? '+' : ''}${wasteChangeSince2005.toFixed(1)}% ${wasteChangeSince2005 > 0 ? 'Increase' : 'Decrease'} since 2005` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <WasteTonnageChart 
                      location={selectedLocation}
                      embedOnly={true}
                      onWasteChangeCalculated={(change) => setWasteChangeDebounced(change)}
                    />
                  </div>
                </div>
              )}
                  {isOverviewChartEnabled('waste', 'landfill_emissions') && (
                <div className="overview-chart-card" style={{ minHeight: '500px', height: '500px' }} onClick={() => handleNavigateWithCheck('/other')}>
                  <h4 className="overview-chart-title-lg">
                    Landfill Emissions{wasteSectionSettled && landfillEmissions2023 !== null ? `: ${landfillEmissions2023.toLocaleString()} tons CO₂e (2023)` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <LandfillEmissionsStackedChart 
                      location={selectedLocation} 
                      embedOnly={true}
                      onEmissionsCalculated={(emissions) => setLandfillEmissionsDebounced(emissions)}
                    />
                  </div>
                </div>
                  )}
                </>
              )}
              </div> : <div style={{ minHeight: '300px' }} />}
              <MoreFiguresButton path="/other" label="More Waste+ Figures" onClick={handleNavigateWithCheck} />
            </div>
          </div>
        )}

        {/* Section 5: People & Equity - Charts Only */}
        {isOverviewSectionEnabled('people_equity') && (
          <div
            className={getSectionClassName('people-equity', 'tool-section tool-section-gray')}
            data-section-id="people-equity"
            ref={(el) => { sectionRefsMap.current['people-equity'] = el; lazyPeopleEquity.sentinelRef.current = el; }}
            style={{ opacity: sectionOpacities['people-equity'] ?? 0, transform: `scale(${0.96 + 0.04 * (sectionOpacities['people-equity'] ?? 0)})` }}
          >
            {SECTION_BG_IMAGES['people-equity'] && <img src={SECTION_BG_IMAGES['people-equity']} alt="" aria-hidden="true" className="overview-section-bg-img" loading="lazy" decoding="async" style={{ opacity: 0.25 * (sectionOpacities['people-equity'] ?? 0) }} />}
            <div className="section-container">
              <h2 className="overview-section-title">
                {renderSampleTitle('People & Equity')}
              </h2>
              {lazyPeopleEquity.shouldMount ? <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('people_equity', 'income_by_race') && (
                <div className="overview-chart-card" style={{ minHeight: '440px', height: '440px', overflow: 'hidden' }} onClick={() => handleNavigateWithCheck('/people')}>
                  <h4 className="overview-chart-title-lg">
                    Income by Race{incomeDifferenceRatio !== null ? ` (${incomeDifferenceRatio.toFixed(1)}x difference)` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <IncomeByRaceBar key={selectedLocation} location={selectedLocation} embedOnly={true} onIncomeDifference={setIncomeDifferenceRatio} />
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('people_equity', 'housing_burden') && (
                <div className="overview-chart-card" style={{ minHeight: '440px', height: '440px', overflow: 'hidden' }} onClick={() => handleNavigateWithCheck('/people')}>
                  <h4 className="overview-chart-title-lg">
                    Households Spending 35%+ of Income on Housing
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <HousingBurdenSection location={selectedLocation} geofips={selectedGeofips ? String(selectedGeofips) : null} embedOnly={true} />
                  </div>
                </div>
              )}
              </div> : <div style={{ minHeight: '300px' }} />}
              <MoreFiguresButton path="/people" label="More People & Equity Figures" onClick={handleNavigateWithCheck} />
            </div>
          </div>
        )}

        {/* Section 6: GHG Inventory */}
        {isOverviewSectionEnabled('ghg_inventory') && (
          <div
            className={getSectionClassName('ghg-inventory', 'tool-section tool-section-white')}
            data-section-id="ghg-inventory"
            ref={(el) => { sectionRefsMap.current['ghg-inventory'] = el; lazyGhg.sentinelRef.current = el; }}
            style={{ opacity: sectionOpacities['ghg-inventory'] ?? 0, transform: `scale(${0.96 + 0.04 * (sectionOpacities['ghg-inventory'] ?? 0)})` }}
          >
            {SECTION_BG_IMAGES['ghg-inventory'] && <img src={SECTION_BG_IMAGES['ghg-inventory']} alt="" aria-hidden="true" className="overview-section-bg-img" loading="lazy" decoding="async" style={{ opacity: 0.25 * (sectionOpacities['ghg-inventory'] ?? 0) }} />}
            <div className="section-container">
              <h2 className="overview-section-title">
                {renderSampleTitle('GHG Inventory')}
              </h2>
              {lazyGhg.shouldMount ? <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('ghg_inventory', 'sector_based_ghg') && (
                <div className="overview-chart-card" style={{ minHeight: '400px', overflow: 'hidden' }} onClick={() => handleNavigateWithCheck('/ghg')}>
                  <h4 className="overview-chart-title-lg">
                    Sector-Based GHG Inventory
                  </h4>
                  {overviewVMTChartLoaded && vmtDataReady && vmtDataAvailable && ghgInventoryData ? (
                    <div style={{ height: '370px', pointerEvents: 'none', flex: 1, overflow: 'hidden' }}>
                      <SectorBasedGHGInventory 
                        location={selectedLocation} 
                        chartType={ghgChartType} 
                        setChartType={setGhgChartType} 
                        ghgData={ghgInventoryData} 
                      />
                    </div>
                  ) : (
                    <div className="overview-text-secondary" style={{ textAlign: 'center', padding: '20px', flex: 1 }}>
                      <p>{overviewVMTChartLoaded ? 'Loading Sector-Based GHG Inventory...' : 'Loading VMT chart data...'}</p>
                    </div>
                  )}
                </div>
              )}
              {isOverviewChartEnabled('ghg_inventory', 'consumption_based_ghg') && (
                <div className="overview-chart-card" style={{ minHeight: '400px', overflow: 'hidden' }} onClick={() => handleNavigateWithCheck('/ghg')}>
                  <h4 className="overview-chart-title-lg">
                    Consumption-Based GHG Inventory
                  </h4>
                  {consumptionGHGData ? (
                    <div style={{ height: '370px', pointerEvents: 'none', flex: 1, overflow: 'hidden' }}>
                      <ConsumptionBasedGHGInventory 
                        location={selectedLocation} 
                        chartType={consumptionChartType} 
                        setChartType={setConsumptionChartType}
                        preloadedData={consumptionGHGData}
                      />
                    </div>
                  ) : (
                    <div className="overview-text-secondary" style={{ textAlign: 'center', padding: '20px', flex: 1 }}>
                      <p>Loading Consumption-Based GHG Inventory...</p>
                    </div>
                  )}
                </div>
              )}
              </div> : <div style={{ minHeight: '300px' }} />}
              <MoreFiguresButton path="/ghg" label="More GHG Inventory Figures" onClick={handleNavigateWithCheck} />
            </div>
          </div>
        )}
      </div>

      {/* Custom sections and datasets for Overview (Local Government content) */}
      {!isLandingPage && selectedLocation && (
        <>
          <CustomSectionsForPage pageId="overview" locationName={selectedLocation} />
          <CustomDatasetsForPage pageId="overview" locationName={selectedLocation} />
        </>
      )}

      {/* Data Download Modal — disabled while MEMBER_EXPORTS_LOCKED; header uses MemberExportLockModal */}
      <DataDownloadModal
        isOpen={!exportsLocked && showDataDownload}
        onClose={() => setShowDataDownload(false)}
        location={selectedLocation || ''}
        geofips={selectedGeofips}
      />
    </div>
  );
};
